import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectTherapistProfile,
  selectClinicProfile,
  selectClinicId,
  setClinicProfile,
  selectIsAdmin,
  selectIsNutritionClinic,
  selectPatientSharingMode,
  selectClinicSharesTreatments,
} from "../../../store/slices/authSlice";
import { useToast } from "../../../contexts/ToastContext";
import usePlanPermissions from "../../../hooks/usePlanPermissions";
import { GlobalLoader, HelpButton } from "../../../components/shared";
import {
  functions,
  db,
} from "../../../firebase";
import { httpsCallable } from "firebase/functions";
import { collection, query, getCountFromServer } from "firebase/firestore";
import {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from "../../../store/services/tasksApi";
import {
  useGetTreatmentTagsQuery,
  useCreateTreatmentTagMutation,
  useUpdateTreatmentTagMutation,
  useDeleteTreatmentTagMutation,
} from "../../../store/services/treatmentsApi";
import {
  useGetClinicGamesQuery,
  useCreateClinicGameMutation,
  useUpdateClinicGameMutation,
  useDeleteClinicGameMutation,
  GAME_GOAL_CATEGORIES,
  getGoalsByIds,
} from "../../../store/services/gamesApi";
import {
  useUpdateClinicMutation,
  useUploadLogoMutation,
  useGetClinicTherapistsQuery,
  useUpdatePatientSharingModeMutation,
} from "../../../store/services/therapistApi";
import {
  useGetRoomsQuery,
  useCreateRoomMutation,
  useUpdateRoomMutation,
  useDeleteRoomMutation,
} from "../../../store/services/roomsApi";
import { useGetActivityLogsQuery } from "../../../store/services/activityLogsApi";
import { ACTION_LABELS } from "../../../firebase";
import RoomScheduleEditor from "../../../components/calendar/RoomScheduleEditor";
import DatePicker from "../../../components/ui/DatePicker";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { SensitiveDataNotice } from "../../../components/shared/access";
import CustomClinikitButton from "../../../components/ui/CustomClinikitButton";
import HourSelect from "../../../components/ui/HourSelect";
import {
  CALENDAR_START_HOUR_OPTIONS,
  getClinicCalendarStartHour,
  normalizeCalendarStartHour,
} from "../../../utils/calendarHours";
import {
  CALENDAR_COLOR_MODES,
  getCalendarColorMode,
} from "../../../utils/appointmentDisplay";
import {
  Buildings,
  UserPlus,
  UserCircle,
  X,
  CheckCircle,
  WarningCircle,
  Globe,
  Phone,
  EnvelopeSimple,
  Translate,
  Timer,
  Info,
  Tag,
  Plus,
  Pencil,
  Key,
  Trash,
  ListChecks,
  Note,
  Stethoscope,
  Lightbulb,
  Plugs,
  PlugsConnected,
  Upload,
  CurrencyCircleDollar,
  Clock,
  MapPin,
  IdentificationCard,
  CaretDown,
  Target,
  Check,
  ChatCircle,
  Brain,
  HandHeart,
  Hand,
  Headset,
  PaperPlaneTilt,
  Heart,
  Sliders,
  Link,
  FileText,
  Palette,
  Users,
  GameController,
  CaretRight,
  CaretLeft,
  Door,
  WheelchairMotion,
  UsersThree,
  Receipt,
  ChartLineUp,
  Eye,
  ArrowSquareOut,
  MagnifyingGlass,
  Funnel,
  User,
  CalendarBlank,
  ArrowsClockwise,
  ClockCounterClockwise,
  CalendarCheck,
  NotePencil,
  Calendar,
  Sparkle,
  ToggleLeft,
  ShieldCheck,
  Export,
  Ticket,
  VideoCamera,
  Prohibit,
} from "@phosphor-icons/react";
import ContentLoader from "../../../components/shared/ContentLoader";
import LoaderLoop from "../../../components/shared/loader/LoaderLoop";
import MorningSetupWizard from "../../../components/morning/MorningSetupWizard";
import MorningSettingsModal from "../../../components/morning/MorningSettingsModal";
import MorningDisconnectModal from "../../../components/morning/MorningDisconnectModal";
import InvoiceQuickModal from "../../../components/morning/InvoiceQuickModal";
import DigitalInvoiceSetupWizard from "../../../components/digitalInvoice/DigitalInvoiceSetupWizard";
import DigitalInvoiceQuickModal from "../../../components/digitalInvoice/DigitalInvoiceQuickModal";
import GrowIntegrationCard from "../../../components/grow/GrowIntegrationCard";
import LeadsWebhookCard from "../../../components/integrations/LeadsWebhookCard";
import {
  DI_DOCUMENT_LABELS,
  DI_PAYMENT_LABELS,
  getAllDocumentTypeOptions as getDIDocTypeOptions,
  formatCurrency as diFormatCurrency,
} from "../../../services/digitalInvoiceService";
import CustomClinikitButtonLight from "../../../components/ui/CustomClinikitButtonLight";
import {
  formatCurrency,
  MORNING_DOCUMENT_LABELS,
  MORNING_PAYMENT_LABELS,
  MORNING_BUSINESS_TYPE_LABELS,
  getDocumentTypeOptions,
  resolveClinicDefaultDocumentType,
} from "../../../services/morningService";
import morningLogo from "../../../assets/morning_logo_minified.png";
import ModalCloseButton from "../../../components/ui/ModalCloseButton";
import TreatmentTypeFormModal from "../../../components/calendar/TreatmentTypeFormModal";
import { getPublicTypeWarnings } from "../../../utils/treatmentTypeRules";
import { isAssignableTherapist } from "../../../config/permissionsRegistry";
import {
  CLINIC_TYPES,
  SUB_SPECIALIZATIONS_BY_TYPE,
  AGE_RANGE_OPTIONS,
  MEETING_DURATIONS,
  VAT_TYPES,
  getPredefinedSubValues,
  getClinicTypeLabel,
} from "../../../data/clinicTypesConfig";
import { logCustomSubSpecializationNotification } from "../../../firebase";
import CustomClinikitActionButton from "../../../components/ui/CustomClinikitActionButton";
import "./ClinicSettingsPage.css";
import "./MorningPage.css";
import "./AdminActivityLogsPage.css";

// Morning documents page size
const MORNING_PAGE_SIZE = 20;

// Activity logs page size
const ACTIVITY_LOGS_PAGE_SIZE = 50;

// Activity logs action filter options
const ACTIVITY_ACTION_OPTIONS = [
  { value: "all", label: "כל הפעולות" },
  { value: "create_appointment", label: "קביעת תור" },
  { value: "update_appointment", label: "עדכון תור" },
  { value: "delete_appointment", label: "מחיקת תור" },
  { value: "create_patient", label: "יצירת מטופל" },
  { value: "update_patient", label: "עדכון מטופל" },
  { value: "delete_patient", label: "מחיקת מטופל" },
  { value: "create_task", label: "יצירת משימה" },
  { value: "update_task", label: "עדכון משימה" },
  { value: "delete_task", label: "מחיקת משימה" },
  { value: "create_reminder", label: "יצירת תזכורת" },
  { value: "update_reminder", label: "עדכון תזכורת" },
  { value: "delete_reminder", label: "מחיקת תזכורת" },
  { value: "create_category", label: "יצירת קטגוריה" },
  { value: "update_category", label: "עדכון קטגוריה" },
  { value: "delete_category", label: "מחיקת קטגוריה" },
  { value: "create_therapist", label: "הוספת מטפל" },
  { value: "update_therapist_status", label: "עדכון סטטוס מטפל" },
];

// Get icon for action type
const getActivityActionIcon = (action) => {
  if (action.includes("appointment")) {
    return <CalendarCheck size={18} weight="fill" />;
  }
  if (action.includes("patient")) {
    return <UserPlus size={18} weight="fill" />;
  }
  if (action.includes("task")) {
    return <ListChecks size={18} weight="fill" />;
  }
  if (action.includes("reminder")) {
    return <NotePencil size={18} weight="fill" />;
  }
  if (action.includes("category")) {
    return <Tag size={18} weight="fill" />;
  }
  if (action.includes("therapist")) {
    return <User size={18} weight="fill" />;
  }
  return <ClockCounterClockwise size={18} weight="fill" />;
};

// Get action type class for styling
const getActivityActionClass = (action) => {
  if (action.includes("create")) return "action-create";
  if (action.includes("update")) return "action-update";
  if (action.includes("delete")) return "action-delete";
  return "action-default";
};

// Format date to Hebrew display for activity logs
const formatActivityDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Clinic types, sub-specializations, age ranges, meeting durations, and VAT types
// are imported from src/data/clinicTypesConfig.js (single source of truth)



// ── Rooms: compact schedule summary for the room card ─────────────────────
// alwaysAvailable → "זמין תמיד"; scheduleTemplate → "א׳–ה׳ · 08:00–20:00"
// (or "לוח מותאם" when days differ); neither → "ללא הגבלת שעות".
const ROOM_WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const ROOM_DAY_LETTERS = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

const getRoomScheduleSummary = (room) => {
  if (room?.alwaysAvailable === true) {
    return { type: "always", text: "זמין תמיד" };
  }
  const tpl = room?.scheduleTemplate;
  if (tpl) {
    const openDays = ROOM_WEEKDAY_KEYS.map((key, index) => ({
      index,
      slots: tpl[key]?.slots || [],
    })).filter((day) => day.slots.length > 0);
    if (!openDays.length) return { type: "custom", text: "לוח מותאם" };

    const signature = (slots) =>
      slots.map((s) => `${s.start}-${s.end}`).join(",");
    const first = openDays[0].slots;
    const uniform =
      first.length === 1 &&
      openDays.every((day) => signature(day.slots) === signature(first));

    const indexes = openDays.map((day) => day.index);
    const consecutive = indexes.every(
      (value, i) => i === 0 || value === indexes[i - 1] + 1,
    );
    const daysText =
      consecutive && indexes.length > 1
        ? `${ROOM_DAY_LETTERS[indexes[0]]}–${ROOM_DAY_LETTERS[indexes[indexes.length - 1]]}`
        : indexes.map((i) => ROOM_DAY_LETTERS[i]).join(", ");

    if (!uniform) return { type: "custom", text: "לוח מותאם" };
    return {
      type: "template",
      text: `${daysText} · ${first[0].start}–${first[0].end}`,
    };
  }
  return { type: "none", text: "ללא הגבלת שעות" };
};

function ClinicSettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const clinicId = useSelector(selectClinicId);
  const isAdmin = useSelector(selectIsAdmin);
  // Couple intake is hidden for nutrition clinics (see selectClinicManagesCouplesAsPatients)
  const isNutritionClinic = useSelector(selectIsNutritionClinic);
  const logoInputRef = useRef(null);
  const { showSuccess, showError } = useToast();

  // Plan permissions hook
  const {
    checkRoom,
    isChecking: isCheckingPlanPermission,
    PlanLimitModalComponent,
  } = usePlanPermissions();

  // Active tab state
  const [activeTab, setActiveTab] = useState("clinic");

  // Calendar start hour (customization tab) — clinic.calendarStartHour, default 05:00
  const [isSavingCalendarStartHour, setIsSavingCalendarStartHour] = useState(false);
  const handleCalendarStartHourChange = async (hour) => {
    const nextHour = normalizeCalendarStartHour(hour);
    if (nextHour === getClinicCalendarStartHour(clinicProfile)) return;
    setIsSavingCalendarStartHour(true);
    try {
      await updateClinic({
        clinicId,
        data: { calendarStartHour: nextHour },
      }).unwrap();
      dispatch(setClinicProfile({ ...clinicProfile, calendarStartHour: nextHour }));
      showSuccess(`היומן יוצג החל מהשעה ${String(nextHour).padStart(2, "0")}:00`);
    } catch (err) {
      console.error("[CK-5060] Error updating calendar start hour:", {
        errorCode: "CK-5060",
        errorMessage: err?.message,
        clinicId,
        calendarStartHour: nextHour,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון שעת פתיחת היומן");
    } finally {
      setIsSavingCalendarStartHour(false);
    }
  };

  // Scheduling policies (multi-clinic v2) — clinic.multiClinicSettings.
  // Absent ⇒ warn-only (existing clinics are never blocked). Hidden for solo
  // clinics. Written as dotted paths so sibling keys are never overwritten.
  const [savingSchedulingPolicy, setSavingSchedulingPolicy] = useState(null);
  const handleSchedulingPolicyChange = async (field, nextValue) => {
    setSavingSchedulingPolicy(field);
    try {
      await updateClinic({
        clinicId,
        data: { [`multiClinicSettings.${field}`]: nextValue === true },
      }).unwrap();
      dispatch(
        setClinicProfile({
          ...clinicProfile,
          multiClinicSettings: {
            ...(clinicProfile?.multiClinicSettings || {}),
            [field]: nextValue === true,
          },
        })
      );
      showSuccess(
        field === "therapistsCanManageLeads"
          ? nextValue
            ? "כל הצוות רואה ועורך את המתעניינים"
            : "רק מנהלים רואים ועורכים את המתעניינים"
          : field === "preventTherapistDoubleBooking"
          ? nextValue
            ? "כפל תורים למטפל/ת ייחסם (מנהלים יוכלו לעקוף)"
            : "כפל תורים למטפל/ת יציג אזהרה בלבד"
          : nextValue
            ? "קביעת תורים בזמן היעדרות תיחסם (מנהלים יוכלו לעקוף)"
            : "קביעת תורים בזמן היעדרות תציג אזהרה בלבד"
      );
    } catch (err) {
      console.error("[CK-5540] Error updating scheduling policy:", {
        errorCode: "CK-5540",
        errorMessage: err?.message,
        clinicId,
        field,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון מדיניות היומן");
    } finally {
      setSavingSchedulingPolicy(null);
    }
  };

  // Calendar color mode (customization tab) — clinic.calendarColorMode,
  // "status" (default) | "treatmentType". Applies to every therapist in the clinic.
  const [isSavingCalendarColorMode, setIsSavingCalendarColorMode] = useState(false);
  const handleCalendarColorModeChange = async (nextMode) => {
    if (nextMode === getCalendarColorMode(clinicProfile)) return;
    setIsSavingCalendarColorMode(true);
    try {
      await updateClinic({
        clinicId,
        data: { calendarColorMode: nextMode },
      }).unwrap();
      dispatch(setClinicProfile({ ...clinicProfile, calendarColorMode: nextMode }));
      showSuccess(
        nextMode === CALENDAR_COLOR_MODES.TREATMENT_TYPE
          ? "היומן יוצבע לפי סוג טיפול"
          : "היומן יוצבע לפי סטטוס אישור הגעה"
      );
    } catch (err) {
      console.error("[CK-5062] Error updating calendar color mode:", {
        errorCode: "CK-5062",
        errorMessage: err?.message,
        clinicId,
        calendarColorMode: nextMode,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון אופן צביעת היומן");
    } finally {
      setIsSavingCalendarColorMode(false);
    }
  };

  // Solo clinics: therapist management is hidden behind an opt-in card.
  // Local UI state only — resets on reload until a therapist is actually added.
  const [showTherapistManagement, setShowTherapistManagement] = useState(false);
  // Rooms: same opt-in gate while the clinic has no rooms yet.
  const [showRoomsManagement, setShowRoomsManagement] = useState(false);

  // RTK Query mutations
  const [updateClinic, { isLoading: isUpdatingClinic }] =
    useUpdateClinicMutation();
  const [uploadLogo, { isLoading: isUploadingLogo }] = useUploadLogoMutation();

  // Clinic info form state
  const [clinicForm, setClinicForm] = useState({
    name: "",
    type: "",
    defaultHourlyRate: "",
    defaultMeetingTime: 45,
    businessId: "",
    licenseNumber: "",
    clinicAddress: "",
    contactPhone: "",
    contactEmail: "",
    website: "",
    vatType: "",
    subSpecializations: [],
    ageRange: [],
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [newLogoFile, setNewLogoFile] = useState(null);
  const [isSavingClinicInfo, setIsSavingClinicInfo] = useState(false);
  const [clinicInfoSaved, setClinicInfoSaved] = useState(false);

  // Support modal state
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [supportSent, setSupportSent] = useState(false);

  // Therapists list state
  // Team list comes from the RTK cache (kept fresh by the Team page mutations).
  const { data: therapists = [] } = useGetClinicTherapistsQuery({ clinicId }, { skip: !clinicId });

  // Patient sharing mode (multi-clinic v2, Phase 3) — clinic.multiClinicSettings.
  // "assigned" (default) | "all". Server callable also auto-sets calendar.viewClinic
  // on therapists without a personal override. Hidden for solo clinics.
  const patientSharingMode = useSelector(selectPatientSharingMode) === "all" ? "all" : "assigned";
  const sharesTreatments = useSelector(selectClinicSharesTreatments);
  const [updatePatientSharingMode, { isLoading: isSavingSharingMode }] =
    useUpdatePatientSharingModeMutation();
  // null | { kind: "mode", nextMode } | { kind: "treatments" }
  const [shareConfirm, setShareConfirm] = useState(null);
  // null (loading) | number | "?" (count failed — confirm still allowed)
  const [sharePatientCount, setSharePatientCount] = useState(null);
  const activeTherapistCount = therapists.filter(isAssignableTherapist).length;

  // Lazy patient count — only fetched when the "all" confirm modal opens.
  useEffect(() => {
    if (shareConfirm?.kind !== "mode" || shareConfirm.nextMode !== "all" || !clinicId) return;
    let cancelled = false;
    setSharePatientCount(null);
    (async () => {
      try {
        const snap = await getCountFromServer(
          query(collection(db, "clinics", clinicId, "patients"))
        );
        if (!cancelled) setSharePatientCount(snap.data().count);
      } catch (err) {
        console.error("[CK-5549] Error counting clinic patients for sharing confirm:", {
          errorCode: "CK-5549",
          errorMessage: err?.message,
          clinicId,
          timestamp: new Date().toISOString(),
        });
        if (!cancelled) setSharePatientCount("?");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareConfirm, clinicId]);

  const applyPatientSharing = async (nextMode, nextIncludesTreatments) => {
    try {
      const res = await updatePatientSharingMode({
        clinicId,
        patientSharingMode: nextMode,
        allSharedIncludesTreatments: nextIncludesTreatments,
      }).unwrap();
      dispatch(
        setClinicProfile({
          ...clinicProfile,
          multiClinicSettings: {
            ...(clinicProfile?.multiClinicSettings || {}),
            patientSharingMode: res?.patientSharingMode || nextMode,
            allSharedIncludesTreatments:
              typeof res?.allSharedIncludesTreatments === "boolean"
                ? res.allSharedIncludesTreatments
                : nextIncludesTreatments,
          },
        })
      );
      if (nextMode === "all" && patientSharingMode !== "all") {
        showSuccess("כל המטופלים משותפים כעת לכל הצוות");
      } else if (nextMode === "assigned" && patientSharingMode !== "assigned") {
        showSuccess("המטפלים רואים כעת רק מטופלים משויכים");
      } else {
        showSuccess(
          nextIncludesTreatments
            ? "דוחות הטיפול משותפים לכל הצוות"
            : "כל מטפל/ת רואה רק דוחות שכתב/ה בעצמו/ה"
        );
      }
    } catch (err) {
      console.error("[CK-5550] Error updating patient sharing mode:", {
        errorCode: "CK-5550",
        errorMessage: err?.message,
        clinicId,
        patientSharingMode: nextMode,
        allSharedIncludesTreatments: nextIncludesTreatments,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון מצב שיתוף המטופלים");
    }
  };

  const handleSharingModeSelect = (nextMode) => {
    if (nextMode === patientSharingMode || isSavingSharingMode) return;
    setShareConfirm({ kind: "mode", nextMode });
  };

  const handleSharesTreatmentsToggle = (checked) => {
    if (isSavingSharingMode) return;
    if (checked) {
      applyPatientSharing("all", true);
    } else {
      setShareConfirm({ kind: "treatments" });
    }
  };

  const closeShareConfirm = () => setShareConfirm(null);
  const confirmShareDialog = () => {
    if (!shareConfirm) return;
    if (shareConfirm.kind === "mode") {
      // Switching to "all": keep the stored treatments flag (default ON).
      applyPatientSharing(
        shareConfirm.nextMode,
        shareConfirm.nextMode === "all"
          ? clinicProfile?.multiClinicSettings?.allSharedIncludesTreatments !== false
          : undefined
      );
    } else {
      applyPatientSharing("all", false);
    }
  };

  // Treatment-type modal (shared with Calendar Settings → booking panel)
  const [treatmentTypeModal, setTreatmentTypeModal] = useState(null); // null | { editing: category|null }

  // Category modal state (tasks / reminders)
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    color: "#3b82f6",
    type: "task",
  });

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Category colors
  const CATEGORY_COLORS = [
    "#3b82f6", // Blue
    "#22c55e", // Green
    "#f59e0b", // Orange
    "#ef4444", // Red
    "#8b5cf6", // Purple
    "#ec4899", // Pink
    "#06b6d4", // Cyan
    "#6b7280", // Gray
  ];

  // Categories queries
  const { data: taskCategories = [], isLoading: isLoadingTaskCategories } =
    useGetCategoriesQuery({ clinicId, type: "task" }, { skip: !clinicId });

  const {
    data: reminderCategories = [],
    isLoading: isLoadingReminderCategories,
  } = useGetCategoriesQuery(
    { clinicId, type: "reminder" },
    { skip: !clinicId }
  );

  const { data: treatmentTypes = [], isLoading: isLoadingTreatmentTypes } =
    useGetCategoriesQuery(
      { clinicId, type: "treatmentType" },
      { skip: !clinicId }
    );

  const [createCategory, { isLoading: isCreatingCategory }] =
    useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdatingCategory }] =
    useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  // Treatment tags queries
  const { data: treatmentTags = [], isLoading: isLoadingTreatmentTags } =
    useGetTreatmentTagsQuery({ clinicId }, { skip: !clinicId });

  const [createTreatmentTag, { isLoading: isCreatingTreatmentTag }] =
    useCreateTreatmentTagMutation();
  const [updateTreatmentTag, { isLoading: isUpdatingTreatmentTag }] =
    useUpdateTreatmentTagMutation();
  const [deleteTreatmentTag] = useDeleteTreatmentTagMutation();

  // Treatment tag modal state
  const [showTreatmentTagModal, setShowTreatmentTagModal] = useState(false);
  const [editingTreatmentTag, setEditingTreatmentTag] = useState(null);
  const [treatmentTagForm, setTreatmentTagForm] = useState({
    name: "",
    color: "#3b82f6",
  });

  // Games/Toys queries
  const { data: clinicGames = [], isLoading: isLoadingGames } =
    useGetClinicGamesQuery({ clinicId }, { skip: !clinicId });

  const [createClinicGame, { isLoading: isCreatingGame }] =
    useCreateClinicGameMutation();
  const [updateClinicGame, { isLoading: isUpdatingGame }] =
    useUpdateClinicGameMutation();
  const [deleteClinicGame] = useDeleteClinicGameMutation();

  // Game modal state
  const [showGameModal, setShowGameModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [gameForm, setGameForm] = useState({
    name: "",
    minAge: "",
    maxAge: "",
    goalIds: [],
  });
  const [expandedCategories, setExpandedCategories] = useState([]);

  // Morning integration wizard state
  const [showMorningWizard, setShowMorningWizard] = useState(false);
  const [showMorningSettings, setShowMorningSettings] = useState(false);
  const [showMorningDisconnect, setShowMorningDisconnect] = useState(false);

  // ============================================
  // Morning Tab State (Documents Management)
  // ============================================
  const [morningDocuments, setMorningDocuments] = useState([]);
  const [isMorningLoading, setIsMorningLoading] = useState(false);
  const [isMorningInitialLoading, setIsMorningInitialLoading] = useState(false);
  const [morningLoadError, setMorningLoadError] = useState(null);
  const [morningCurrentPage, setMorningCurrentPage] = useState(1);
  const [morningTotalCount, setMorningTotalCount] = useState(0);
  const morningTotalPages = Math.ceil(morningTotalCount / MORNING_PAGE_SIZE);
  const [morningSearchQuery, setMorningSearchQuery] = useState("");
  const [morningFilterType, setMorningFilterType] = useState("all");
  const [morningDateFrom, setMorningDateFrom] = useState("");
  const [morningDateTo, setMorningDateTo] = useState("");
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);

  // Check if Morning is integrated
  const isMorningIntegrated = clinicProfile?.isMorningPaymentSystemIntegrated === true;
  const morningIntegrationInfo = clinicProfile?.morningIntegration;

  // ============================================
  // Digital Invoice Tab State
  // ============================================
  const [showDIWizard, setShowDIWizard] = useState(false);
  const [diDocuments, setDIDocuments] = useState([]);
  const [isDILoading, setIsDILoading] = useState(false);
  const [isDIInitialLoading, setIsDIInitialLoading] = useState(false);
  const [diLoadError, setDILoadError] = useState(null);
  const [diCurrentPage, setDICurrentPage] = useState(1);
  const [diTotalCount, setDITotalCount] = useState(0);
  const DI_PAGE_SIZE = 20;
  const diTotalPages = Math.ceil(diTotalCount / DI_PAGE_SIZE);
  const [diSearchQuery, setDISearchQuery] = useState("");
  const [diFilterType, setDIFilterType] = useState(0);
  const [diDateFrom, setDIDateFrom] = useState("");
  const [diDateTo, setDIDateTo] = useState("");
  const [showDICreateModal, setShowDICreateModal] = useState(false);

  const isDIIntegrated = clinicProfile?.isDigitalInvoiceIntegrated === true;
  const diIntegrationInfo = clinicProfile?.digitalInvoiceIntegration;

  // ============================================
  // Activity Logs Tab State
  // ============================================
  const [activitySelectedTherapist, setActivitySelectedTherapist] = useState("all");
  const [activitySelectedAction, setActivitySelectedAction] = useState("all");
  const [activityStartDate, setActivityStartDate] = useState("");
  const [activityEndDate, setActivityEndDate] = useState("");
  const [activityCurrentPage, setActivityCurrentPage] = useState(1);
  const [activityCursors, setActivityCursors] = useState([]);
  const [activityCurrentCursor, setActivityCurrentCursor] = useState(null);

  // Room management state
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomForm, setRoomForm] = useState({
    name: "",
    capacity: 1,
    isAccessible: false,
    defaultTherapistId: "",
    allowOthersToBook: true,
    blockOnHolidays: true,
    hasCustomAddress: false,
    customAddress: "",
  });
  // Room whose schedule editor (availability modal) is open
  const [scheduleRoom, setScheduleRoom] = useState(null);

  // Rooms queries
  const { data: rooms = [], isLoading: isLoadingRooms } = useGetRoomsQuery(
    { clinicId },
    { skip: !clinicId }
  );

  const [createRoom, { isLoading: isCreatingRoom }] = useCreateRoomMutation();
  const [updateRoom, { isLoading: isUpdatingRoom }] = useUpdateRoomMutation();
  const [deleteRoom] = useDeleteRoomMutation();

  // Activity logs query - only fetch when tab is active
  const { data: activityLogsData, isLoading: isLoadingActivityLogs, isFetching: isFetchingActivityLogs } = useGetActivityLogsQuery(
    {
      clinicId,
      therapistId: activitySelectedTherapist === "all" ? undefined : activitySelectedTherapist,
      action: activitySelectedAction === "all" ? undefined : activitySelectedAction,
      startDate: activityStartDate || undefined,
      endDate: activityEndDate || undefined,
      cursor: activityCurrentCursor,
      pageSize: ACTIVITY_LOGS_PAGE_SIZE,
    },
    { skip: !clinicId || activeTab !== "activity" }
  );

  const activityLogs = activityLogsData?.logs || [];
  const activityHasMore = activityLogsData?.hasMore || false;
  const activityNextCursor = activityLogsData?.nextCursor;

  // Custom duration state
  const [isCustomDurationSelected, setIsCustomDurationSelected] =
    useState(false);
  const [customDurationValue, setCustomDurationValue] = useState("");

  // Initialize clinic form from profile
  useEffect(() => {
    if (clinicProfile) {
      // Check if saved time is a custom value (not one of the predefined values)
      const isCustom =
        clinicProfile.defaultMeetingTime &&
        ![15, 30, 45, 60].includes(clinicProfile.defaultMeetingTime);

      setClinicForm({
        name: clinicProfile.name || "",
        type: clinicProfile.type || "",
        defaultHourlyRate: clinicProfile.defaultHourlyRate || "",
        defaultMeetingTime: clinicProfile.defaultMeetingTime || 45,
        businessId: clinicProfile.businessId || "",
        licenseNumber: clinicProfile.licenseNumber || "",
        clinicAddress: clinicProfile.clinicAddress || "",
        contactPhone: clinicProfile.contactPhone || "",
        contactEmail: clinicProfile.contactEmail || "",
        website: clinicProfile.website || "",
        vatType: clinicProfile.vatType || "",
        subSpecializations: clinicProfile.subSpecializations || [],
        ageRange: clinicProfile.ageRange || [],
      });
      setLogoPreview(clinicProfile.logoUrl || null);

      // Set custom duration state if applicable
      setIsCustomDurationSelected(isCustom);
      setCustomDurationValue(
        isCustom ? clinicProfile.defaultMeetingTime.toString() : ""
      );
    }
  }, [clinicProfile]);


  // ============================================
  // Morning Tab Functions
  // ============================================
  const loadMorningDocuments = useCallback(
    async (page = 1, resetSearch = false, showFullLoader = false) => {
      if (!clinicId || !isMorningIntegrated) return;

      if (showFullLoader) {
        setIsMorningInitialLoading(true);
      } else {
        setIsMorningLoading(true);
      }
      setMorningLoadError(null);

      try {
        const getMorningDocuments = httpsCallable(functions, "getMorningDocuments");

        const filters = {
          page: page,
          pageSize: MORNING_PAGE_SIZE,
        };

        if (!resetSearch && morningSearchQuery.trim()) {
          filters.search = morningSearchQuery.trim();
        }

        if (!resetSearch && morningFilterType !== "all") {
          filters.type = Number(morningFilterType);
        }

        if (!resetSearch && morningDateFrom) {
          filters.fromDate = morningDateFrom;
        }
        if (!resetSearch && morningDateTo) {
          filters.toDate = morningDateTo;
        }

        const result = await getMorningDocuments({ clinicId, filters });

        if (result.data.success) {
          setMorningDocuments(result.data.documents || []);
          setMorningTotalCount(result.data.totalCount || 0);
          setMorningCurrentPage(result.data.page || 1);
        } else {
          console.error("[CK-8811] Error loading documents:", {
            errorCode: "CK-8811",
            error: result.data.error,
            morningError: result.data.morningError,
            timestamp: new Date().toISOString(),
          });
          setMorningLoadError(result.data.error || "שגיאה בטעינת מסמכים");
        }
      } catch (err) {
        console.error("[CK-8811] Error loading documents:", {
          errorCode: "CK-8811",
          errorMessage: err.message,
          timestamp: new Date().toISOString(),
        });
        setMorningLoadError("שגיאה בטעינת מסמכים. נסה שוב.");
      } finally {
        setIsMorningLoading(false);
        setIsMorningInitialLoading(false);
      }
    },
    [clinicId, isMorningIntegrated, morningSearchQuery, morningFilterType, morningDateFrom, morningDateTo]
  );

  // Load morning documents when tab is activated
  useEffect(() => {
    if (activeTab === "morning" && isMorningIntegrated && clinicId) {
      loadMorningDocuments(1, false, true);
    }
  }, [activeTab, isMorningIntegrated, clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMorningSearch = () => {
    setMorningCurrentPage(1);
    loadMorningDocuments(1);
  };

  const handleMorningSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleMorningSearch();
    }
  };

  const handleMorningClearFilters = () => {
    setMorningSearchQuery("");
    setMorningFilterType("all");
    setMorningDateFrom("");
    setMorningDateTo("");
    setMorningCurrentPage(1);
    loadMorningDocuments(1, true);
  };

  const handleMorningPageChange = (newPage) => {
    if (newPage < 1 || newPage > morningTotalPages) return;
    setMorningCurrentPage(newPage);
    loadMorningDocuments(newPage);
  };

  const handleInvoiceSuccess = () => {
    setShowCreateInvoiceModal(false);
    loadMorningDocuments(1, true, true);
  };

  // ============================================
  // Digital Invoice Tab Functions
  // ============================================
  const handleLoadDIDocuments = useCallback(
    async (page = 1, resetSearch = false, showFullLoader = false) => {
      if (!clinicId || !isDIIntegrated) return;

      if (showFullLoader) {
        setIsDIInitialLoading(true);
      } else {
        setIsDILoading(true);
      }
      setDILoadError(null);

      try {
        const getDIDocs = httpsCallable(functions, "getDigitalInvoiceDocuments");

        const filters = {
          page: page,
          pageSize: DI_PAGE_SIZE,
        };

        if (!resetSearch && diSearchQuery.trim()) {
          filters.search = diSearchQuery.trim();
        }
        if (!resetSearch && diFilterType > 0) {
          filters.docType = diFilterType;
        }
        if (!resetSearch && diDateFrom) {
          filters.fromDate = diDateFrom;
        }
        if (!resetSearch && diDateTo) {
          filters.toDate = diDateTo;
        }

        const result = await getDIDocs({ clinicId, filters });

        if (result.data.success) {
          setDIDocuments(result.data.documents || []);
          setDITotalCount(result.data.totalCount || 0);
          setDICurrentPage(result.data.page || page);
        } else {
          setDILoadError(result.data.error || "שגיאה בטעינת מסמכים");
        }
      } catch (err) {
        console.error("[CK-9213] Error loading DI documents:", {
          errorMessage: err.message,
          timestamp: new Date().toISOString(),
        });
        setDILoadError("שגיאה בטעינת מסמכים. נסה שוב.");
      } finally {
        setIsDILoading(false);
        setIsDIInitialLoading(false);
      }
    },
    [clinicId, isDIIntegrated, diSearchQuery, diFilterType, diDateFrom, diDateTo, DI_PAGE_SIZE]
  );

  useEffect(() => {
    if (activeTab === "digitalInvoice" && isDIIntegrated && clinicId) {
      handleLoadDIDocuments(1, false, true);
    }
  }, [activeTab, isDIIntegrated, clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDIInvoiceSuccess = () => {
    setShowDICreateModal(false);
    handleLoadDIDocuments(1, true, true);
  };

  const formatMorningDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleViewMorningDocument = (doc) => {
    if (doc.pdfUrl) {
      window.open(doc.pdfUrl, "_blank");
    } else {
      window.open("https://app.greeninvoice.co.il/documents", "_blank");
    }
  };

  const handleOpenMorning = () => {
    window.open("https://app.greeninvoice.co.il", "_blank");
  };

  // ============================================
  // Activity Logs Tab Functions
  // ============================================
  
  // Reset pagination when filters change
  useEffect(() => {
    setActivityCurrentPage(1);
    setActivityCursors([]);
    setActivityCurrentCursor(null);
  }, [activitySelectedTherapist, activitySelectedAction, activityStartDate, activityEndDate]);

  const handleActivityTherapistChange = (e) => {
    setActivitySelectedTherapist(e.target.value);
  };

  const handleActivityActionChange = (e) => {
    setActivitySelectedAction(e.target.value);
  };

  const handleActivityStartDateChange = (e) => {
    setActivityStartDate(e.target.value);
  };

  const handleActivityEndDateChange = (e) => {
    setActivityEndDate(e.target.value);
  };

  const clearActivityFilters = () => {
    setActivitySelectedTherapist("all");
    setActivitySelectedAction("all");
    setActivityStartDate("");
    setActivityEndDate("");
  };

  const handleActivityNextPage = () => {
    if (activityHasMore && activityNextCursor) {
      setActivityCursors([...activityCursors, activityCurrentCursor]);
      setActivityCurrentCursor(activityNextCursor);
      setActivityCurrentPage(activityCurrentPage + 1);
    }
  };

  const handleActivityPrevPage = () => {
    if (activityCurrentPage > 1) {
      const newCursors = [...activityCursors];
      const prevCursor = newCursors.pop();
      setActivityCursors(newCursors);
      setActivityCurrentCursor(prevCursor);
      setActivityCurrentPage(activityCurrentPage - 1);
    }
  };

  const hasActiveActivityFilters = 
    activitySelectedTherapist !== "all" || 
    activitySelectedAction !== "all" || 
    activityStartDate || 
    activityEndDate;

  // Clinic info handlers
  const handleClinicFormChange = (field, value) => {
    setClinicForm((prev) => ({ ...prev, [field]: value }));
    setClinicInfoSaved(false);
  };

  const handleTypeSelect = (value) => {
    setClinicForm((prev) => ({
      ...prev,
      type: value,
      subSpecializations: [], // Reset sub-specializations when type changes
    }));
    setClinicInfoSaved(false);
  };

  const handleDurationSelect = (value) => {
    if (value === "custom") {
      setIsCustomDurationSelected(true);
      // Keep current custom value or set to empty
      if (customDurationValue) {
        setClinicForm((prev) => ({
          ...prev,
          defaultMeetingTime: parseInt(customDurationValue, 10),
        }));
      }
    } else {
      setIsCustomDurationSelected(false);
      setClinicForm((prev) => ({ ...prev, defaultMeetingTime: value }));
    }
    setClinicInfoSaved(false);
  };

  const handleCustomDurationChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCustomDurationValue(value);
    if (value) {
      setClinicForm((prev) => ({
        ...prev,
        defaultMeetingTime: parseInt(value, 10),
      }));
    }
    setClinicInfoSaved(false);
  };

  const toggleSubSpecialization = (value) => {
    setClinicForm((prev) => ({
      ...prev,
      subSpecializations: prev.subSpecializations.includes(value)
        ? prev.subSpecializations.filter((s) => s !== value)
        : [...prev.subSpecializations, value],
    }));
    setClinicInfoSaved(false);
  };

  const toggleAgeRange = (value) => {
    setClinicForm((prev) => ({
      ...prev,
      ageRange: prev.ageRange.includes(value)
        ? prev.ageRange.filter((a) => a !== value)
        : [...prev.ageRange, value],
    }));
    setClinicInfoSaved(false);
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setNewLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
      setClinicInfoSaved(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setNewLogoFile(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    setClinicInfoSaved(false);
  };

  const handleSaveClinicInfo = async () => {
    setIsSavingClinicInfo(true);
    try {
      // Upload new logo if selected
      let logoUrl = clinicProfile?.logoUrl;
      if (newLogoFile) {
        const result = await uploadLogo({
          clinicId,
          file: newLogoFile,
        }).unwrap();
        logoUrl = result.logoUrl;
      } else if (!logoPreview && clinicProfile?.logoUrl) {
        // Logo was removed
        logoUrl = null;
      }

      // Update clinic document
      const updateData = {
        name: clinicForm.name,
        type: clinicForm.type,
        defaultHourlyRate: clinicForm.defaultHourlyRate
          ? Number(clinicForm.defaultHourlyRate)
          : null,
        defaultMeetingTime: clinicForm.defaultMeetingTime,
        businessId: clinicForm.businessId || null,
        licenseNumber: clinicForm.licenseNumber?.trim() || null,
        clinicAddress: clinicForm.clinicAddress || null,
        // Letterhead contact details (PDF exports) — optional
        contactPhone: clinicForm.contactPhone?.trim() || null,
        contactEmail: clinicForm.contactEmail?.trim().toLowerCase() || null,
        website: clinicForm.website?.trim() || null,
        vatType: clinicForm.vatType || null,
        subSpecializations: clinicForm.subSpecializations,
        ageRange: clinicForm.ageRange,
        specializations: clinicForm.type ? [clinicForm.type] : [],
      };

      if (logoUrl !== undefined) {
        updateData.logoUrl = logoUrl;
      }

      await updateClinic({ clinicId, data: updateData }).unwrap();

      // Update local state
      dispatch(setClinicProfile({ ...clinicProfile, ...updateData }));
      setNewLogoFile(null);
      setClinicInfoSaved(true);
      showSuccess("פרטי הקליניקה נשמרו בהצלחה");

      // Hide success message after 3 seconds
      setTimeout(() => setClinicInfoSaved(false), 3000);
    } catch (error) {
      console.error("[CK-5020] Error saving clinic info:", {
        errorCode: "CK-5020",
        errorMessage: error.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת פרטי הקליניקה");
    } finally {
      setIsSavingClinicInfo(false);
    }
  };

  // Support request handler
  const handleSupportRequest = async () => {
    setIsSendingSupport(true);
    try {
      // Use mailto as a simple solution (opens email client)
      const subject = encodeURIComponent(
        `בקשת תמיכה - ${clinicProfile?.name || "קליניקה"}`
      );
      const body = encodeURIComponent(
        `בקשת תמיכה חדשה:\n\n` +
          `שם הקליניקה: ${clinicProfile?.name || "לא צוין"}\n` +
          `מזהה קליניקה: ${clinicId}\n` +
          `שם המטפל: ${therapistProfile?.firstName} ${therapistProfile?.lastName}\n` +
          `טלפון: ${therapistProfile?.phone || "לא צוין"}\n` +
          `אימייל: ${therapistProfile?.email || "לא צוין"}\n\n` +
          `המשתמש ביקש שצוות התמיכה יצור איתו קשר.`
      );

      // Open mailto link
      window.location.href = `mailto:office@clinikit.co.il?subject=${subject}&body=${body}`;

      setSupportSent(true);
      setTimeout(() => {
        setShowSupportModal(false);
        setSupportSent(false);
      }, 2500);
    } catch (error) {
      console.error("[CK-5021] Error sending support request:", error);
    } finally {
      setIsSendingSupport(false);
    }
  };

  // Category handlers
  const handleOpenCategoryModal = (category = null, type = "task") => {
    // Treatment types have their own modal (rate, duration, public-booking flags).
    if ((category?.type || type) === "treatmentType") {
      setTreatmentTypeModal({ editing: category });
      return;
    }
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        color: category.color,
        type: category.type,
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: "",
        color: "#3b82f6",
        type: type,
      });
    }
    setShowCategoryModal(true);
  };

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryForm({
      name: "",
      color: "#3b82f6",
      type: "task",
    });
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) return;

    try {
      if (editingCategory) {
        await updateCategory({
          clinicId,
          categoryId: editingCategory.id,
          categoryData: {
            name: categoryForm.name.trim(),
            color: categoryForm.color,
          },
        }).unwrap();
      } else {
        await createCategory({
          clinicId,
          categoryData: {
            name: categoryForm.name.trim(),
            color: categoryForm.color,
            type: categoryForm.type,
          },
        }).unwrap();
      }
      handleCloseCategoryModal();
      showSuccess(
        editingCategory ? "הקטגוריה עודכנה בהצלחה" : "הקטגוריה נוצרה בהצלחה"
      );
    } catch (error) {
      console.error("[CK-4006] Error saving category:", {
        errorCode: "CK-4006",
        errorMessage: error.message,
        clinicId,
        categoryId: editingCategory?.id,
        isEditing: !!editingCategory,
        categoryType: categoryForm.type,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDeleteCategory = (category) => {
    setConfirmDialog({
      isOpen: true,
      title: "מחיקת קטגוריה",
      message: `האם אתה בטוח שברצונך למחוק את הקטגוריה "${category.name}"?`,
      onConfirm: async () => {
        try {
          await deleteCategory({ clinicId, categoryId: category.id }).unwrap();
        } catch (error) {
          console.error("[CK-4007] Error deleting category:", {
            errorCode: "CK-4007",
            errorMessage: error.message,
            clinicId,
            categoryId: category.id,
            timestamp: new Date().toISOString(),
          });
        }
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  // Treatment tag handlers
  const handleOpenTreatmentTagModal = (tag = null) => {
    if (tag) {
      setEditingTreatmentTag(tag);
      setTreatmentTagForm({
        name: tag.name,
        color: tag.color,
      });
    } else {
      setEditingTreatmentTag(null);
      setTreatmentTagForm({
        name: "",
        color: "#3b82f6",
      });
    }
    setShowTreatmentTagModal(true);
  };

  const handleCloseTreatmentTagModal = () => {
    setShowTreatmentTagModal(false);
    setEditingTreatmentTag(null);
    setTreatmentTagForm({
      name: "",
      color: "#3b82f6",
    });
  };

  const handleSaveTreatmentTag = async () => {
    if (!treatmentTagForm.name.trim()) return;

    try {
      if (editingTreatmentTag) {
        await updateTreatmentTag({
          clinicId,
          tagId: editingTreatmentTag.id,
          tagData: {
            name: treatmentTagForm.name.trim(),
            color: treatmentTagForm.color,
          },
        }).unwrap();
      } else {
        await createTreatmentTag({
          clinicId,
          tagData: {
            name: treatmentTagForm.name.trim(),
            color: treatmentTagForm.color,
          },
        }).unwrap();
      }
      handleCloseTreatmentTagModal();
      showSuccess(
        editingTreatmentTag ? "התגית עודכנה בהצלחה" : "התגית נוצרה בהצלחה"
      );
    } catch (error) {
      console.error("[CK-5010] Error saving treatment tag:", {
        errorCode: "CK-5010",
        errorMessage: error.message,
        clinicId,
        tagId: editingTreatmentTag?.id,
        isEditing: !!editingTreatmentTag,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת התגית");
    }
  };

  const handleDeleteTreatmentTag = (tag) => {
    setConfirmDialog({
      isOpen: true,
      title: "מחיקת תגית",
      message: `האם אתה בטוח שברצונך למחוק את התגית "${tag.name}"?`,
      onConfirm: async () => {
        try {
          await deleteTreatmentTag({ clinicId, tagId: tag.id }).unwrap();
        } catch (error) {
          console.error("[CK-5011] Error deleting treatment tag:", {
            errorCode: "CK-5011",
            errorMessage: error.message,
            clinicId,
            tagId: tag.id,
            timestamp: new Date().toISOString(),
          });
        }
      },
    });
  };

  // Game handlers
  const handleOpenGameModal = (game = null) => {
    if (game) {
      setEditingGame(game);
      setGameForm({
        name: game.name,
        minAge: game.minAge?.toString() || "",
        maxAge: game.maxAge?.toString() || "",
        goalIds: game.goalIds || [],
      });
    } else {
      setEditingGame(null);
      setGameForm({
        name: "",
        minAge: "",
        maxAge: "",
        goalIds: [],
      });
    }
    setExpandedCategories([]);
    setShowGameModal(true);
  };

  const handleCloseGameModal = () => {
    setShowGameModal(false);
    setEditingGame(null);
    setGameForm({
      name: "",
      minAge: "",
      maxAge: "",
      goalIds: [],
    });
    setExpandedCategories([]);
  };

  const handleToggleGoal = (goalId) => {
    setGameForm((prev) => ({
      ...prev,
      goalIds: prev.goalIds.includes(goalId)
        ? prev.goalIds.filter((id) => id !== goalId)
        : [...prev.goalIds, goalId],
    }));
  };

  const handleToggleCategory = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSaveGame = async () => {
    if (!gameForm.name.trim()) return;

    try {
      const gameData = {
        name: gameForm.name.trim(),
        minAge: gameForm.minAge ? parseInt(gameForm.minAge, 10) : null,
        maxAge: gameForm.maxAge ? parseInt(gameForm.maxAge, 10) : null,
        goalIds: gameForm.goalIds,
      };

      if (editingGame) {
        await updateClinicGame({
          clinicId,
          gameId: editingGame.id,
          gameData,
        }).unwrap();
      } else {
        await createClinicGame({
          clinicId,
          gameData,
        }).unwrap();
      }
      handleCloseGameModal();
      showSuccess(editingGame ? "המשחק עודכן בהצלחה" : "המשחק נוסף בהצלחה");
    } catch (error) {
      console.error("[CK-5012] Error saving game:", {
        errorCode: "CK-5012",
        errorMessage: error.message,
        clinicId,
        gameId: editingGame?.id,
        isEditing: !!editingGame,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת המשחק");
    }
  };

  const handleDeleteGame = (game) => {
    setConfirmDialog({
      isOpen: true,
      title: "מחיקת משחק",
      message: `האם אתה בטוח שברצונך למחוק את המשחק "${game.name}"?`,
      onConfirm: async () => {
        try {
          await deleteClinicGame({ clinicId, gameId: game.id }).unwrap();
          showSuccess("המשחק נמחק בהצלחה");
        } catch (error) {
          console.error("[CK-5013] Error deleting game:", {
            errorCode: "CK-5013",
            errorMessage: error.message,
            clinicId,
            gameId: game.id,
            timestamp: new Date().toISOString(),
          });
          showError("שגיאה במחיקת המשחק");
        }
      },
    });
  };

  const availableSubSpecs = SUB_SPECIALIZATIONS_BY_TYPE[clinicForm.type] || [];
  const predefinedSubValues = getPredefinedSubValues(clinicForm.type);

  // "Other" custom sub-specialization state
  const [isOtherSubActive, setIsOtherSubActive] = useState(false);
  const [otherSubValue, setOtherSubValue] = useState("");
  const [otherSubError, setOtherSubError] = useState("");

  const handleAddCustomSub = async () => {
    const trimmed = otherSubValue.trim();
    setOtherSubError("");

    if (trimmed.length < 2) {
      setOtherSubError("מינימום 2 תווים");
      return;
    }
    if (trimmed.length > 50) {
      setOtherSubError("מקסימום 50 תווים");
      return;
    }

    const allExistingValues = [
      ...Array.from(predefinedSubValues),
      ...clinicForm.subSpecializations,
    ];
    const allExistingLabels = availableSubSpecs.map((s) => s.label);

    if (
      allExistingValues.includes(trimmed) ||
      allExistingLabels.includes(trimmed)
    ) {
      setOtherSubError("התמחות זו כבר קיימת");
      return;
    }

    setClinicForm((prev) => ({
      ...prev,
      subSpecializations: [...prev.subSpecializations, trimmed],
    }));

    try {
      await logCustomSubSpecializationNotification({
        clinicId,
        clinicName: clinicProfile?.name || clinicForm.name,
        clinicType: clinicForm.type,
        clinicTypeLabel: getClinicTypeLabel(clinicForm.type),
        therapistName: `${therapistProfile?.firstName || ""} ${therapistProfile?.lastName || ""}`.trim(),
        therapistEmail: therapistProfile?.email || "",
        customValue: trimmed,
      });
    } catch (err) {
      console.error("[CK-5025] Error logging custom sub notification:", err);
    }

    setOtherSubValue("");
    setIsOtherSubActive(false);
  };

  // Room handlers
  const handleOpenRoomModal = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setRoomForm({
        name: room.name,
        capacity: room.capacity || 1,
        isAccessible: room.isAccessible || false,
        defaultTherapistId: room.defaultTherapistId || "",
        allowOthersToBook: room.allowOthersToBook !== false,
        blockOnHolidays: room.blockOnHolidays === true,
        hasCustomAddress: room.hasCustomAddress === true,
        customAddress: room.customAddress || "",
      });
    } else {
      setEditingRoom(null);
      setRoomForm({
        name: "",
        capacity: 1,
        isAccessible: false,
        defaultTherapistId: "",
        allowOthersToBook: true,
        blockOnHolidays: true,
        hasCustomAddress: false,
        customAddress: "",
      });
    }
    setShowRoomModal(true);
  };

  const handleCloseRoomModal = () => {
    setShowRoomModal(false);
    setEditingRoom(null);
    setRoomForm({
      name: "",
      capacity: 1,
      isAccessible: false,
      defaultTherapistId: "",
      allowOthersToBook: true,
      blockOnHolidays: true,
      hasCustomAddress: false,
      customAddress: "",
    });
  };

  const handleSaveRoom = async () => {
    if (!roomForm.name.trim()) return;

    if (roomForm.hasCustomAddress && !roomForm.customAddress.trim()) {
      showError("יש להזין כתובת לחדר");
      return;
    }

    // Check plan permission for creating new rooms (not for editing)
    if (!editingRoom) {
      const canProceed = await checkRoom();
      if (!canProceed) {
        // Modal is shown automatically by the hook
        return;
      }
    }

    try {
      const roomData = {
        name: roomForm.name.trim(),
        capacity: parseInt(roomForm.capacity, 10) || 1,
        isAccessible: roomForm.isAccessible,
        defaultTherapistId: roomForm.defaultTherapistId || null,
        allowOthersToBook: roomForm.allowOthersToBook,
        blockOnHolidays: roomForm.blockOnHolidays,
        hasCustomAddress: roomForm.hasCustomAddress,
        customAddress: roomForm.hasCustomAddress
          ? roomForm.customAddress.trim()
          : "",
      };

      if (editingRoom) {
        await updateRoom({
          clinicId,
          roomId: editingRoom.id,
          roomData,
          therapistId: therapistProfile?.uid,
          therapistName: `${therapistProfile?.firstName} ${therapistProfile?.lastName}`,
        }).unwrap();
      } else {
        await createRoom({
          clinicId,
          roomData,
          therapistId: therapistProfile?.uid,
          therapistName: `${therapistProfile?.firstName} ${therapistProfile?.lastName}`,
        }).unwrap();
      }

      showSuccess(editingRoom ? "החדר עודכן בהצלחה" : "החדר נוצר בהצלחה");
      handleCloseRoomModal();
    } catch (error) {
      console.error("[CK-9200] Error saving room:", {
        errorCode: "CK-9200",
        errorMessage: error.message,
        clinicId,
        roomId: editingRoom?.id,
        isEditing: !!editingRoom,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת החדר");
    }
  };

  const handleDeleteRoom = (room) => {
    setConfirmDialog({
      isOpen: true,
      title: "מחיקת חדר",
      message: `האם אתה בטוח שברצונך למחוק את החדר "${room.name}"? פגישות קיימות המשויכות לחדר זה יישארו ללא שיוך לחדר.`,
      onConfirm: async () => {
        try {
          await deleteRoom({
            clinicId,
            roomId: room.id,
            roomName: room.name,
            therapistId: therapistProfile?.uid,
            therapistName: `${therapistProfile?.firstName} ${therapistProfile?.lastName}`,
          }).unwrap();
          showSuccess("החדר נמחק בהצלחה");
        } catch (error) {
          console.error("[CK-9201] Error deleting room:", {
            errorCode: "CK-9201",
            errorMessage: error.message,
            clinicId,
            roomId: room.id,
            timestamp: new Date().toISOString(),
          });
          showError("שגיאה במחיקת החדר");
        }
      },
    });
  };

  // Get therapist name by ID for display
  const getTherapistNameById = (therapistId) => {
    if (!therapistId) return null;
    const therapist = therapists.find((t) => t.uid === therapistId);
    return therapist ? `${therapist.firstName} ${therapist.lastName}` : null;
  };

  return (
    <div className="clinic-settings-page">
      {/* Global loader for permission check */}
      {isCheckingPlanPermission && <GlobalLoader message="בודק הרשאות..." />}

      {/* Plan limit modal */}
      <PlanLimitModalComponent />

      <div className="clinic-settings-header">
        <div className="header-title">
          <Buildings size={28} weight="duotone" className="header-icon" />
          <h1>הגדרות קליניקה</h1>
        </div>
        <div className="clinic-settings-header-actions">
          <HelpButton featureKey="clinic_settings" />
          <button
            className="support-btn"
            onClick={() => setShowSupportModal(true)}
          >
            <Headset size={20} weight="bold" />
            <span>צריכים עזרה?</span>
          </button>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === "clinic" ? "active" : ""}`}
          onClick={() => setActiveTab("clinic")}
        >
          <Buildings size={20} weight="regular" />
          <span>קליניקה</span>
        </button>
        <button
          className={`settings-tab ${
            activeTab === "customization" ? "active" : ""
          }`}
          onClick={() => setActiveTab("customization")}
        >
          <Sliders size={20} weight="regular" />
          <span>התאמה אישית</span>
        </button>
        <button
          className={`settings-tab ${
            activeTab === "permissions" ? "active" : ""
          }`}
          onClick={() => setActiveTab("permissions")}
        >
          <ShieldCheck size={20} weight="regular" />
          <span>הגדרות מתקדמות</span>
        </button>
        <button
          className={`settings-tab ${
            activeTab === "integrations" ? "active" : ""
          }`}
          onClick={() => setActiveTab("integrations")}
        >
          <Link size={20} weight="regular" />
          <span>אינטגרציות</span>
        </button>
        {isMorningIntegrated && (
          <button
            className={`settings-tab ${
              activeTab === "morning" ? "active" : ""
            }`}
            onClick={() => setActiveTab("morning")}
          >
            <Receipt size={20} weight="regular" />
            <span>ניהול מורנינג</span>
          </button>
        )}
        {isDIIntegrated && (
          <button
            className={`settings-tab ${
              activeTab === "digitalInvoice" ? "active" : ""
            }`}
            onClick={() => setActiveTab("digitalInvoice")}
          >
            <Receipt size={20} weight="regular" />
            <span>ניהול חשבוניות</span>
          </button>
        )}
        <button
          className={`settings-tab ${
            activeTab === "activity" ? "active" : ""
          }`}
          onClick={() => setActiveTab("activity")}
        >
          <ClockCounterClockwise size={20} weight="regular" />
          <span>תיעודי פעילות</span>
        </button>
      </div>

      <div className="clinic-settings-container">
        <div className="clinic-settings-content">
          {/* ============================================
              TAB 1: CLINIC INFO
              ============================================ */}
          {activeTab === "clinic" && (
            <div className="tab-content clinic-tab">
              {/* Clinic Basic Info Card */}
              <section className="ck-adv-card">
                <div className="ck-adv-card-header">
                  <div className="ck-adv-card-title">
                    <span className="ck-adv-card-icon">
                      <Buildings size={20} weight="fill" />
                    </span>
                    <div className="ck-adv-card-title-text">
                      <h2>פרטי הקליניקה</h2>
                      <p className="ck-adv-card-desc">
                        מידע בסיסי על הקליניקה שלכם
                      </p>
                    </div>
                  </div>
                  {clinicInfoSaved && (
                    <div className="save-success-badge">
                      <CheckCircle size={18} weight="fill" />
                      <span>נשמר בהצלחה!</span>
                    </div>
                  )}
                </div>

                <div className="clinic-info-form">
                  {/* Logo & Name Row */}
                  <div className="logo-name-row">
                    <div className="logo-section">
                      <label>לוגו הקליניקה</label>
                      <div
                        className={`logo-upload-area ${
                          logoPreview ? "has-logo" : ""
                        }`}
                        onClick={() => logoInputRef.current?.click()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            logoInputRef.current?.click();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={
                          logoPreview
                            ? "החלפת לוגו הקליניקה"
                            : "העלאת לוגו הקליניקה"
                        }
                      >
                        {logoPreview ? (
                          <div className="logo-preview">
                            <img src={logoPreview} alt="לוגו הקליניקה" />
                            <div className="logo-edit-overlay">
                              <Pencil size={16} weight="fill" />
                              <span>החלפת לוגו</span>
                            </div>
                            <button
                              type="button"
                              className="remove-logo-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveLogo();
                              }}
                              aria-label="הסרת לוגו"
                            >
                              <X size={14} weight="bold" />
                            </button>
                          </div>
                        ) : (
                          <div className="upload-placeholder">
                            <Upload size={24} />
                            <span>העלאת לוגו</span>
                          </div>
                        )}
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleLogoSelect}
                          style={{ display: "none" }}
                        />
                      </div>
                    </div>

                    <div className="name-section">
                      <div className="form-group">
                        <label>שם הקליניקה</label>
                        <input
                          type="text"
                          value={clinicForm.name}
                          onChange={(e) =>
                            handleClinicFormChange("name", e.target.value)
                          }
                          placeholder='לדוגמה: "קליניקת השפה"'
                          className="clinic-name-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>סוג הפרקטיקה</label>
                        <div className="type-options">
                          {CLINIC_TYPES.map((type) => {
                            const Icon = type.icon;
                            return (
                              <button
                                key={type.value}
                                type="button"
                                className={`type-option ${
                                  clinicForm.type === type.value
                                    ? "selected"
                                    : ""
                                } disabled`}
                                disabled
                              >
                                <Icon
                                  size={18}
                                  weight={
                                    clinicForm.type === type.value
                                      ? "fill"
                                      : "regular"
                                  }
                                />
                                <span>{type.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Business Settings Row */}
                  <div className="business-settings-row">
                    <div className="form-group">
                      <label>
                        <CurrencyCircleDollar size={16} />
                        תעריף ברירת מחדל (₪)
                      </label>
                      <input
                        type="number"
                        value={clinicForm.defaultHourlyRate}
                        onChange={(e) =>
                          handleClinicFormChange(
                            "defaultHourlyRate",
                            e.target.value
                          )
                        }
                        placeholder="350"
                        dir="ltr"
                      />
                      <span className="field-hint">
                        ברירת מחדל לפגישות חדשות
                      </span>
                    </div>

                    <div className="form-group">
                      <label>
                        <Clock size={16} />
                        משך טיפול ממוצע
                      </label>
                      <div className="duration-options">
                        {MEETING_DURATIONS.map((duration) =>
                          duration.value === "custom" ? (
                            <div
                              key={duration.value}
                              className={`duration-btn duration-custom-btn ${
                                isCustomDurationSelected ? "selected" : ""
                              }`}
                              onClick={() => handleDurationSelect("custom")}
                            >
                              {isCustomDurationSelected ? (
                                <div className="custom-duration-input-wrapper">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={customDurationValue}
                                    onChange={handleCustomDurationChange}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="דק׳"
                                    className="custom-duration-input"
                                    autoFocus
                                  />
                                </div>
                              ) : (
                                <span>{duration.label}</span>
                              )}
                            </div>
                          ) : (
                            <button
                              key={duration.value}
                              type="button"
                              className={`duration-btn ${
                                !isCustomDurationSelected &&
                                clinicForm.defaultMeetingTime === duration.value
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                handleDurationSelect(duration.value)
                              }
                            >
                              {duration.label}
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>
                        <IdentificationCard size={16} />
                        ח״פ / עוסק מורשה
                      </label>
                      <input
                        type="text"
                        value={clinicForm.businessId}
                        onChange={(e) =>
                          handleClinicFormChange("businessId", e.target.value)
                        }
                        placeholder="512345678"
                        dir="ltr"
                      />
                      <span className="field-hint">חובה להפקת חשבוניות</span>
                    </div>

                    <div className="form-group">
                      <label>
                        <IdentificationCard size={16} />
                        מספר רישיון
                      </label>
                      <input
                        type="text"
                        value={clinicForm.licenseNumber}
                        onChange={(e) =>
                          handleClinicFormChange("licenseNumber", e.target.value)
                        }
                        placeholder="לדוגמה: 12345"
                        dir="ltr"
                      />
                      <span className="field-hint">
                        אם קיים, יופיע בכותרת טפסים חתומים שיוצאו ל-PDF
                      </span>
                    </div>
                  </div>

                  {/* Address & VAT Row */}
                  <div className="address-vat-row">
                    <div className="form-group address-group">
                      <label>
                        <MapPin size={16} />
                        כתובת הקליניקה
                      </label>
                      <input
                        type="text"
                        value={clinicForm.clinicAddress}
                        onChange={(e) =>
                          handleClinicFormChange(
                            "clinicAddress",
                            e.target.value
                          )
                        }
                        placeholder="רחוב, מספר, עיר"
                      />
                      <span className="field-hint">
                        יופיע בכותרת מסמכים וחשבוניות
                      </span>
                    </div>


                    <div className="form-group vat-group">
                      <label>הגדרת מע״מ</label>
                      <div className="select-wrapper">
                        <select
                          value={clinicForm.vatType}
                          onChange={(e) =>
                            handleClinicFormChange("vatType", e.target.value)
                          }
                        >
                          <option value="">בחרו סוג עוסק</option>
                          {VAT_TYPES.map((vat) => (
                            <option key={vat.value} value={vat.value}>
                              {vat.label}
                            </option>
                          ))}
                        </select>
                        <CaretDown size={14} className="select-icon" />
                      </div>
                      <span className="field-hint">
                        משפיע על חישובים פיננסיים
                      </span>
                    </div>
                  </div>

                  {/* Letterhead contact details (PDF exports) */}
                  <div className="form-row clinic-contact-row">
                    <div className="form-group">
                      <label>
                        <Phone size={16} />
                        טלפון הקליניקה
                      </label>
                      <input
                        type="tel"
                        value={clinicForm.contactPhone}
                        onChange={(e) =>
                          handleClinicFormChange("contactPhone", e.target.value)
                        }
                        placeholder="03-1234567"
                        dir="ltr"
                      />
                      <span className="field-hint">לכותרת מסמכים שיוצאו ל-PDF</span>
                    </div>

                    <div className="form-group">
                      <label>
                        <EnvelopeSimple size={16} />
                        אימייל הקליניקה
                      </label>
                      <input
                        type="email"
                        value={clinicForm.contactEmail}
                        onChange={(e) =>
                          handleClinicFormChange("contactEmail", e.target.value)
                        }
                        placeholder="clinic@example.co.il"
                        dir="ltr"
                      />
                      <span className="field-hint">לכותרת מסמכים שיוצאו ל-PDF</span>
                    </div>

                    <div className="form-group">
                      <label>
                        <Globe size={16} />
                        אתר
                      </label>
                      <input
                        type="url"
                        value={clinicForm.website}
                        onChange={(e) =>
                          handleClinicFormChange("website", e.target.value)
                        }
                        placeholder="www.example.co.il"
                        dir="ltr"
                      />
                      <span className="field-hint">אופציונלי — לכותרת מסמכים</span>
                    </div>
                  </div>

                  {/* Specializations */}
                  {clinicForm.type && (
                    <div className="specializations-section">
                      <div className="form-group">
                        <label>
                          <Target size={16} />
                          תתי-התמחויות
                        </label>
                        <p className="field-description">
                          בחרו את התחומים הספציפיים בהם אתם מתמחים
                        </p>
                        <div className="chips-container">
                          {availableSubSpecs.map((spec) => (
                            <button
                              key={spec.value}
                              type="button"
                              className={`chip ${
                                clinicForm.subSpecializations.includes(
                                  spec.value
                                )
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                toggleSubSpecialization(spec.value)
                              }
                            >
                              {clinicForm.subSpecializations.includes(
                                spec.value
                              ) && <Check size={14} weight="bold" />}
                              <span>{spec.label}</span>
                            </button>
                          ))}

                          {/* Custom (user-added) sub-specializations */}
                          {clinicForm.subSpecializations
                            .filter((s) => !predefinedSubValues.has(s))
                            .map((customSub) => (
                              <button
                                key={customSub}
                                type="button"
                                className="chip selected"
                                onClick={() => toggleSubSpecialization(customSub)}
                              >
                                <Check size={14} weight="bold" />
                                <span>{customSub}</span>
                              </button>
                            ))}

                          {/* "Other" chip / input */}
                          {!isOtherSubActive ? (
                            <button
                              type="button"
                              className="chip chip-other"
                              onClick={() => setIsOtherSubActive(true)}
                            >
                              <Plus size={14} weight="bold" />
                              <span>אחר</span>
                            </button>
                          ) : (
                            <div className="chip-other-input-wrapper">
                              <input
                                type="text"
                                className={`chip-other-input ${otherSubError ? "error" : ""}`}
                                value={otherSubValue}
                                onChange={(e) => {
                                  setOtherSubValue(e.target.value);
                                  setOtherSubError("");
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddCustomSub();
                                  }
                                  if (e.key === "Escape") {
                                    setIsOtherSubActive(false);
                                    setOtherSubValue("");
                                    setOtherSubError("");
                                  }
                                }}
                                placeholder="הקלידו התמחות..."
                                autoFocus
                                maxLength={50}
                              />
                              <button
                                type="button"
                                className="chip-other-add-btn"
                                onClick={handleAddCustomSub}
                              >
                                הוסף
                              </button>
                              {otherSubError && (
                                <span className="chip-other-error">{otherSubError}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>קבוצות גיל</label>
                        <p className="field-description">
                          עם אילו קבוצות גיל אתם עובדים?
                        </p>
                        <div className="chips-container">
                          {AGE_RANGE_OPTIONS.map((age) => (
                            <button
                              key={age.value}
                              type="button"
                              className={`chip ${
                                clinicForm.ageRange.includes(age.value)
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => toggleAgeRange(age.value)}
                            >
                              {clinicForm.ageRange.includes(age.value) && (
                                <Check size={14} weight="bold" />
                              )}
                              <span>{age.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="save-section">
                    <CustomClinikitButton
                      variant="primary"
                      size="large"
                      onClick={handleSaveClinicInfo}
                      disabled={isSavingClinicInfo || isUploadingLogo}
                    >
                      {isSavingClinicInfo || isUploadingLogo ? (
                        <>
                          <LoaderLoop size="xs" />
                          <span>שומר...</span>
                        </>
                      ) : (
                        <span>עדכון פרטי קליניקה</span>
                      )}
                    </CustomClinikitButton>
                  </div>
                </div>
              </section>

            </div>
          )}

          {/* ============================================
              TAB: THERAPIST PERMISSIONS
              ============================================ */}
          {activeTab === "permissions" && (
            <div className="tab-content permissions-tab">
              {/* Therapist Management Card — gated for solo clinics */}
              {!clinicProfile?.isSoloClinic || showTherapistManagement ? (
              <section className="ck-adv-card">
                <div className="ck-adv-card-header">
                  <div className="ck-adv-card-title">
                    <span className="ck-adv-card-icon">
                      <Users size={20} weight="fill" />
                    </span>
                    <div className="ck-adv-card-title-text">
                      <h2>ניהול צוות</h2>
                      <p className="ck-adv-card-desc">
                        אנשי צוות, תפקידים והרשאות — וברירות מחדל לכל המטפלים
                      </p>
                    </div>
                  </div>
                  <CustomClinikitActionButton
                    icon={<UserPlus size={16} weight="bold" />}
                    onClick={() => navigate("/dashboard/admin/team?add=1")}
                  >
                    הוספת איש/אשת צוות
                  </CustomClinikitActionButton>
                </div>

                {/* Multi-clinic v2: team management moved to its own page */}
                <div className="ck-team-moved">
                  <div className="ck-team-moved-text">
                    <strong>ניהול הצוות עבר לעמוד חדש</strong>
                    <span>
                      תפקידים (מנהל/ת, מטפל/ת, מזכירות), הרשאות אישיות לכל איש/אשת צוות,
                      העברת מטופלים והשבתה — הכל בעמוד "צוות הקליניקה".
                      {therapists.length > 0 && ` כרגע ${therapists.filter((t) => t.isActive !== false).length} אנשי צוות פעילים.`}
                    </span>
                  </div>
                  <CustomClinikitActionButton
                    icon={<UsersThree size={16} weight="bold" />}
                    onClick={() => navigate("/dashboard/admin/team")}
                  >
                    לעמוד צוות הקליניקה
                  </CustomClinikitActionButton>
                </div>

                {clinicProfile?.isSoloClinic && (
                  <div className="ck-adv-footnote">
                    <Info size={14} weight="fill" />
                    <span>
                      קליניקה זו מוגדרת כקליניקת מטפל יחיד. הוספת מטפל תשנה
                      הגדרה זו.
                    </span>
                  </div>
                )}

                {/* Patient sharing mode (multi-clinic v2) — hidden for solo clinics */}
                {!clinicProfile?.isSoloClinic && (
                  <div className="ck-adv-subsection ck-share-subsection">
                    <div className="ck-adv-subsection-header">
                      <UsersThree size={16} weight="fill" />
                      <div className="ck-adv-subsection-title">
                        <h3>שיתוף מטופלים בקליניקה</h3>
                        <p>מי מהצוות רואה אילו מטופלים</p>
                      </div>
                    </div>

                    <div
                      className={`ck-share-seg${isSavingSharingMode ? " ck-share-seg-busy" : ""}`}
                      role="radiogroup"
                      aria-label="מצב שיתוף מטופלים"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={patientSharingMode === "assigned"}
                        className={`ck-share-opt${patientSharingMode === "assigned" ? " active" : ""}`}
                        disabled={isSavingSharingMode}
                        onClick={() => handleSharingModeSelect("assigned")}
                      >
                        <span className="ck-share-opt-title">
                          {patientSharingMode === "assigned" ? (
                            <CheckCircle size={15} weight="fill" />
                          ) : (
                            <User size={15} weight="regular" />
                          )}
                          מטופל משויך למטפל/ת
                        </span>
                        <span className="ck-share-opt-caption">
                          המודל הנוכחי — כל מטפל/ת רואה את המטופלים המשויכים אליו/ה. ההרשאה 'שיוך מטופל לכמה מטפלים' למטה שולטת בשיתוף נקודתי.
                        </span>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={patientSharingMode === "all"}
                        className={`ck-share-opt${patientSharingMode === "all" ? " active" : ""}`}
                        disabled={isSavingSharingMode}
                        onClick={() => handleSharingModeSelect("all")}
                      >
                        <span className="ck-share-opt-title">
                          {patientSharingMode === "all" ? (
                            <CheckCircle size={15} weight="fill" />
                          ) : (
                            <UsersThree size={15} weight="regular" />
                          )}
                          כל המטופלים משותפים לכל הצוות
                        </span>
                        <span className="ck-share-opt-caption">
                          כל מטפל/ת רואה ויכול/ה לערוך את כל המטופלים והקבצים בקליניקה. תורים של מטפלים אחרים — לצפייה בלבד, אלא אם ניתנה הרשאה 'קביעה ועריכה של תורים למטפלים אחרים'.
                        </span>
                      </button>
                    </div>

                    {patientSharingMode === "all" && (
                      <div className="permissions-list">
                        <div className="permission-row">
                          <div className="permission-row-right">
                            <div className="permission-icon ck-share-icon">
                              <FileText size={16} weight="duotone" />
                            </div>
                            <div className="permission-info">
                              <div className="permission-title-row">
                                <span className="permission-title">כולל דוחות טיפול</span>
                              </div>
                              <span className="permission-desc">
                                כשהאפשרות כבויה, כל מטפל/ת רואה רק דוחות שכתב/ה בעצמו/ה.
                              </span>
                            </div>
                          </div>
                          <label className={`ck-perm-pill-switch${isSavingSharingMode ? " ck-perm-pill-switch-disabled" : ""}`}>
                            <input
                              type="checkbox"
                              checked={sharesTreatments}
                              disabled={isSavingSharingMode}
                              onChange={(e) => handleSharesTreatmentsToggle(e.target.checked === true)}
                            />
                            <span className="ck-perm-pill-switch-track"></span>
                          </label>
                        </div>
                        <SensitiveDataNotice compact />
                      </div>
                    )}

                    <div className="ck-adv-footnote">
                      <Info size={14} weight="fill" />
                      <span>
                        ניתן לתת הרשאת צפייה בכל המטופלים למטפל/ת בודד/ת בעמוד צוות הקליניקה.
                      </span>
                    </div>
                  </div>
                )}

                {/* Therapist Permissions Sub-section */}
                <div className="ck-adv-subsection">
                  <div className="ck-adv-subsection-header">
                    <ShieldCheck size={16} weight="fill" />
                    <div className="ck-adv-subsection-title">
                      <h3>ברירות מחדל לכל המטפלים</h3>
                      <p>
                        חלות על כל מטפל/ת שאין לו/לה הרשאה אישית
                      </p>
                    </div>
                  </div>
                  <div className="ck-adv-footnote">
                    <Info size={14} weight="fill" />
                    <span>
                      ניתן לשנות הרשאות למטפל ספציפי בעמוד צוות הקליניקה. הרשאה אישית גוברת על ברירת המחדל.
                    </span>
                  </div>

                <div className="permissions-list">
                  {/* Finance Access */}
                  <div className="permission-row">
                    <div className="permission-row-right">
                      <div className="permission-icon finance">
                        <CurrencyCircleDollar size={16} weight="duotone" />
                      </div>
                      <div className="permission-info">
                        <div className="permission-title-row">
                          <span className="permission-title">גישה לפיננסים</span>
                          <div className="permission-info-btn">
                            <Info size={15} weight="fill" className="permission-info-icon" />
                            <div className="permission-info-tooltip">
                              <span className="permission-info-tooltip-title">גישה לפיננסים</span>
                              <p>כאשר כבוי, מטפלים לא יראו מידע פיננסי כלל.</p>
                              <p>הם יוכלו לקבוע טיפולים ולבחור סוגי טיפול, אך לא יראו סכומים ותעריפים — המערכת תשתמש בסכום המוגדר מאחורי הקלעים.</p>
                              <p>בסיום טיפול, המטפל יעדכן סטטוס ויתעד תשלום. מעקב הפיננסים והגבייה יתבצע מחשבון הקליניקה.</p>
                            </div>
                          </div>
                        </div>
                        <span className="permission-desc">
                          {clinicProfile?.therapistPermissions?.allowFinanceAccess === true || clinicProfile?.therapistPermissions?.allowFinanceAccess === undefined
                            ? "מטפלים יכולים לנהל תשלומים, לראות סכומים ותעריפים"
                            : "רק מנהלים רואים ומנהלים את הפיננסים"}
                        </span>
                      </div>
                    </div>
                    <label className="ck-perm-pill-switch">
                      <input
                        type="checkbox"
                        checked={clinicProfile?.therapistPermissions?.allowFinanceAccess === true || clinicProfile?.therapistPermissions?.allowFinanceAccess === undefined}
                        onChange={async (e) => {
                          const newValue = e.target.checked === true;
                          const updatedPermissions = {
                            ...clinicProfile?.therapistPermissions,
                            allowFinanceAccess: newValue,
                          };
                          if (!newValue) {
                            updatedPermissions.allowCreateInvoices = false;
                          }
                          try {
                            await updateClinic({
                              clinicId,
                              data: { therapistPermissions: updatedPermissions },
                            }).unwrap();
                            dispatch(
                              setClinicProfile({
                                ...clinicProfile,
                                therapistPermissions: updatedPermissions,
                              })
                            );
                            showSuccess(
                              newValue
                                ? "מטפלים יכולים כעת לגשת לפיננסים"
                                : "גישה לפיננסים הוגבלה למנהלים בלבד"
                            );
                          } catch (err) {
                            showError("שגיאה בעדכון ההרשאה");
                          }
                        }}
                      />
                      <span className="ck-perm-pill-switch-track"></span>
                    </label>
                  </div>

                  {/* Create Invoices — only if Digital Invoice is integrated */}
                  {isDIIntegrated && (
                    <div className={`permission-row${!(clinicProfile?.therapistPermissions?.allowFinanceAccess === true || clinicProfile?.therapistPermissions?.allowFinanceAccess === undefined) ? " permission-disabled" : ""}`}>
                      <div className="permission-row-right">
                        <div className="permission-icon invoices">
                          <Receipt size={16} weight="duotone" />
                        </div>
                        <div className="permission-info">
                          <div className="permission-title-row">
                            <span className="permission-title">הפקת חשבוניות</span>
                            <div className="permission-info-btn">
                              <Info size={15} weight="fill" className="permission-info-icon" />
                              <div className="permission-info-tooltip">
                                <span className="permission-info-tooltip-title">הפקת חשבוניות</span>
                                <p>מאפשר למטפלים להפיק חשבוניות תחת הקליניקה באמצעות חשבונית דיגיטלית.</p>
                                {!(clinicProfile?.therapistPermissions?.allowFinanceAccess === true || clinicProfile?.therapistPermissions?.allowFinanceAccess === undefined) && (
                                  <p className="tooltip-note">יש להפעיל גישה לפיננסים כדי לאפשר הרשאה זו.</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className="permission-desc">
                            {!(clinicProfile?.therapistPermissions?.allowFinanceAccess === true || clinicProfile?.therapistPermissions?.allowFinanceAccess === undefined)
                              ? "דורש הפעלת גישה לפיננסים"
                              : clinicProfile?.therapistPermissions?.allowCreateInvoices === true || clinicProfile?.therapistPermissions?.allowCreateInvoices === undefined
                                ? "מטפלים יכולים להפיק חשבוניות בשם העסק"
                                : "רק מנהלים יכולים להפיק חשבוניות"}
                          </span>
                        </div>
                      </div>
                      <label className={`ck-perm-pill-switch${!(clinicProfile?.therapistPermissions?.allowFinanceAccess === true || clinicProfile?.therapistPermissions?.allowFinanceAccess === undefined) ? " ck-perm-pill-switch-disabled" : ""}`}>
                        <input
                          type="checkbox"
                          disabled={!(clinicProfile?.therapistPermissions?.allowFinanceAccess === true || clinicProfile?.therapistPermissions?.allowFinanceAccess === undefined)}
                          checked={
                            (clinicProfile?.therapistPermissions?.allowFinanceAccess === true || clinicProfile?.therapistPermissions?.allowFinanceAccess === undefined) &&
                            (clinicProfile?.therapistPermissions?.allowCreateInvoices === true || clinicProfile?.therapistPermissions?.allowCreateInvoices === undefined)
                          }
                          onChange={async (e) => {
                            const newValue = e.target.checked === true;
                            try {
                              await updateClinic({
                                clinicId,
                                data: {
                                  therapistPermissions: {
                                    ...clinicProfile?.therapistPermissions,
                                    allowCreateInvoices: newValue,
                                  },
                                },
                              }).unwrap();
                              dispatch(
                                setClinicProfile({
                                  ...clinicProfile,
                                  therapistPermissions: {
                                    ...clinicProfile?.therapistPermissions,
                                    allowCreateInvoices: newValue,
                                  },
                                })
                              );
                              showSuccess(
                                newValue
                                  ? "מטפלים יכולים כעת להפיק חשבוניות"
                                  : "הפקת חשבוניות הוגבלה למנהלים בלבד"
                              );
                            } catch (err) {
                              showError("שגיאה בעדכון ההרשאה");
                            }
                          }}
                        />
                        <span className="ck-perm-pill-switch-track"></span>
                      </label>
                    </div>
                  )}

                  {/* Export Data */}
                  <div className="permission-row">
                    <div className="permission-row-right">
                      <div className="permission-icon export">
                        <Export size={16} weight="duotone" />
                      </div>
                      <div className="permission-info">
                        <div className="permission-title-row">
                          <span className="permission-title">ייצוא נתונים</span>
                          <div className="permission-info-btn">
                            <Info size={15} weight="fill" className="permission-info-icon" />
                            <div className="permission-info-tooltip">
                              <span className="permission-info-tooltip-title">ייצוא נתונים</span>
                              <p>מאפשר למטפלים לייצא נתונים מהמקומות השונים באפליקציה — רשימות מטופלים, דוחות טיפולים ונתונים נוספים.</p>
                            </div>
                          </div>
                        </div>
                        <span className="permission-desc">
                          {clinicProfile?.therapistPermissions?.allowExportData === true || clinicProfile?.therapistPermissions?.allowExportData === undefined
                            ? "מטפלים יכולים לייצא דוחות ונתונים"
                            : "רק מנהלים יכולים לייצא דוחות ונתונים"}
                        </span>
                      </div>
                    </div>
                    <label className="ck-perm-pill-switch">
                      <input
                        type="checkbox"
                        checked={clinicProfile?.therapistPermissions?.allowExportData === true || clinicProfile?.therapistPermissions?.allowExportData === undefined}
                        onChange={async (e) => {
                          const newValue = e.target.checked === true;
                          try {
                            await updateClinic({
                              clinicId,
                              data: {
                                therapistPermissions: {
                                  ...clinicProfile?.therapistPermissions,
                                  allowExportData: newValue,
                                },
                              },
                            }).unwrap();
                            dispatch(
                              setClinicProfile({
                                ...clinicProfile,
                                therapistPermissions: {
                                  ...clinicProfile?.therapistPermissions,
                                  allowExportData: newValue,
                                },
                              })
                            );
                            showSuccess(
                              newValue
                                ? "מטפלים יכולים כעת לייצא נתונים"
                                : "ייצוא נתונים הוגבל למנהלים בלבד"
                            );
                          } catch (err) {
                            showError("שגיאה בעדכון ההרשאה");
                          }
                        }}
                      />
                      <span className="ck-perm-pill-switch-track"></span>
                    </label>
                  </div>

                  {/* Multi-Therapist Assignment */}
                  <div className="permission-row">
                    <div className="permission-row-right">
                      <div className="permission-icon multi-therapist">
                        <UsersThree size={16} weight="duotone" />
                      </div>
                      <div className="permission-info">
                        <div className="permission-title-row">
                          <span className="permission-title">שיוך מטופל לכמה מטפלים</span>
                          <div className="permission-info-btn">
                            <Info size={15} weight="fill" className="permission-info-icon" />
                            <div className="permission-info-tooltip">
                              <span className="permission-info-tooltip-title">שיוך מטופל לכמה מטפלים</span>
                              <p>כאשר מופעל, ניתן לשייך מטופל אחד ליותר ממטפל אחד בקליניקה.</p>
                              <p>כל מטפל משויך יראה את המטופל ברשימת המטופלים שלו ויוכל לקבוע לו פגישות.</p>
                              <p>ניתן לנהל את רשימת המטפלים המשויכים מפרופיל המטופל.</p>
                            </div>
                          </div>
                        </div>
                        <span className="permission-desc">
                          {clinicProfile?.therapistPermissions?.allowMultipleTherapists === true
                            ? "מטופל יכול להיות משויך לכמה מטפלים בו-זמנית"
                            : "כל מטופל משויך למטפל אחד בלבד"}
                        </span>
                      </div>
                    </div>
                    <label className="ck-perm-pill-switch">
                      <input
                        type="checkbox"
                        checked={clinicProfile?.therapistPermissions?.allowMultipleTherapists === true}
                        onChange={async (e) => {
                          const newValue = e.target.checked === true;
                          if (!newValue) {
                            try {
                              const { getDocs, collection, query, where } = await import("firebase/firestore");
                              const { db } = await import("../../../firebase");
                              const patientsRef = collection(db, "clinics", clinicId, "patients");
                              const q = query(patientsRef, where("isDraft", "==", false));
                              const snapshot = await getDocs(q);
                              const hasMulti = snapshot.docs.some((doc) => {
                                const data = doc.data();
                                return data.therapistIds && data.therapistIds.length > 1;
                              });
                              if (hasMulti) {
                                showError("לא ניתן לבטל — ישנם מטופלים המשויכים ליותר ממטפל אחד. יש לשנות את השיוך שלהם לפני ביטול ההגדרה.");
                                return;
                              }
                            } catch (checkErr) {
                              console.error("[CK-9401] Error checking multi-therapist patients:", checkErr);
                              showError("שגיאה בבדיקת מטופלים. נסה שוב.");
                              return;
                            }
                          }
                          const updatedPermissions = {
                            ...clinicProfile?.therapistPermissions,
                            allowMultipleTherapists: newValue,
                          };
                          try {
                            await updateClinic({
                              clinicId,
                              data: { therapistPermissions: updatedPermissions },
                            }).unwrap();
                            dispatch(
                              setClinicProfile({
                                ...clinicProfile,
                                therapistPermissions: updatedPermissions,
                              })
                            );
                            showSuccess(
                              newValue
                                ? "ניתן כעת לשייך מטופל לכמה מטפלים"
                                : "שיוך מטופל הוגבל למטפל אחד בלבד"
                            );
                          } catch (err) {
                            showError("שגיאה בעדכון ההגדרה");
                          }
                        }}
                      />
                      <span className="ck-perm-pill-switch-track"></span>
                    </label>
                  </div>
                </div>

                  <div className="ck-adv-footnote">
                    <Info size={14} weight="fill" />
                    <span>
                      ההרשאות חלות על מטפלים רגילים בלבד. מנהלים ובעלי הקליניקה תמיד רואים הכל.
                    </span>
                  </div>
                </div>
              </section>
              ) : (
              <section className="ck-adv-card ck-multi-therapist-gate">
                <div className="ck-adv-card-header">
                  <div className="ck-adv-card-title">
                    <span className="ck-adv-card-icon">
                      <UsersThree size={20} weight="fill" />
                    </span>
                    <div className="ck-adv-card-title-text">
                      <h2>ניהול מטפלים</h2>
                      <p className="ck-adv-card-desc">
                        הקליניקה שלך מוגדרת כרגע כקליניקת מטפל יחיד
                      </p>
                    </div>
                  </div>
                </div>

                <p className="ck-mt-gate-lead">
                  רוצה לצרף מטפלים נוספים לקליניקה? כך זה עובד:
                </p>
                <ul className="ck-mt-gate-list">
                  <li>
                    <span className="ck-mt-gate-li-icon">
                      <UserCircle size={16} weight="fill" />
                    </span>
                    <span>
                      אתה נשאר מטפל בקליניקה — היומן, המטופלים והדוחות האישיים
                      שלך לא משתנים.
                    </span>
                  </li>
                  <li>
                    <span className="ck-mt-gate-li-icon">
                      <ChartLineUp size={16} weight="fill" />
                    </span>
                    <span>
                      בתפריט הצד יתווסף אזור &quot;ניהול הקליניקה&quot;: תמונת
                      מצב, ניהול מטופלים, יומנים ותורים וניהול משימות ותזכורות —
                      לכל המטפלים יחד.
                    </span>
                  </li>
                  <li>
                    <span className="ck-mt-gate-li-icon">
                      <ShieldCheck size={16} weight="fill" />
                    </span>
                    <span>
                      תוכל להוסיף מטפלים, להגדיר להם הרשאות (כספים, חשבוניות,
                      ייצוא) ולשייך להם מטופלים.
                    </span>
                  </li>
                  <li>
                    <span className="ck-mt-gate-li-icon">
                      <CheckCircle size={16} weight="fill" />
                    </span>
                    <span>
                      השינוי נכנס לתוקף רק כשתוסיף בפועל את המטפל הראשון.
                    </span>
                  </li>
                </ul>
                <div className="ck-mt-gate-actions">
                  <CustomClinikitActionButton
                    icon={<UserPlus size={16} weight="bold" />}
                    onClick={() => setShowTherapistManagement(true)}
                  >
                    אני רוצה לאפשר מטפלים נוספים בקליניקה
                  </CustomClinikitActionButton>
                </div>
              </section>
              )}

              {/* Rooms Management Card — gated while the clinic has no rooms */}
              {isLoadingRooms || rooms.length > 0 || showRoomsManagement ? (
              <section className="ck-adv-card">
                <div className="ck-adv-card-header">
                  <div className="ck-adv-card-title">
                    <span className="ck-adv-card-icon">
                      <Door size={20} weight="fill" />
                    </span>
                    <div className="ck-adv-card-title-text">
                      <h2>ניהול חדרים</h2>
                      <p className="ck-adv-card-desc">
                        הגדר חדרי טיפול בקליניקה לתיאום פגישות לפי חדרים
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <CustomClinikitActionButton
                      icon={<Door size={16} weight="bold" />}
                      onClick={() => handleOpenRoomModal()}
                    >
                      הוספת חדר
                    </CustomClinikitActionButton>
                  )}
                </div>

                {isLoadingRooms ? (
                  <ContentLoader message="טוען חדרים..." />
                ) : rooms.length > 0 ? (
                  <div className="rm-grid">
                    {rooms.map((room) => {
                      const schedule = getRoomScheduleSummary(room);
                      return (
                        <div key={room.id} className="rm-card">
                          <div className="rm-card-top">
                            <div className="rm-card-heading">
                              <h4 className="rm-name">{room.name}</h4>
                              <div className="rm-chips">
                                <span className="rm-chip">
                                  <UsersThree size={11} weight="fill" />
                                  קיבולת {room.capacity || 1}
                                </span>
                                {room.isAccessible && (
                                  <span className="rm-chip accessible">
                                    <WheelchairMotion
                                      size={11}
                                      weight="fill"
                                    />
                                    נגיש
                                  </span>
                                )}
                                {room.blockOnHolidays === true && (
                                  <span className="rm-chip holidays">
                                    <Prohibit size={11} weight="fill" />
                                    נחסם בחגים
                                  </span>
                                )}
                              </div>
                            </div>
                            {isAdmin && (
                              <div className="rm-card-actions">
                                <button
                                  type="button"
                                  className="rm-icon-btn"
                                  onClick={() => handleOpenRoomModal(room)}
                                  title="עריכת חדר"
                                  aria-label="עריכת חדר"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="rm-icon-btn danger"
                                  onClick={() => handleDeleteRoom(room)}
                                  title="מחיקת חדר"
                                  aria-label="מחיקת חדר"
                                >
                                  <Trash size={14} />
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="rm-card-details">
                            <div className="rm-detail">
                              <Clock size={11} weight="fill" />
                              <span
                                className={`rm-detail-text ${
                                  schedule.type === "always" ? "highlight" : ""
                                }`}
                              >
                                {schedule.text}
                              </span>
                            </div>
                            <div className="rm-detail">
                              <User size={11} weight="fill" />
                              <span className="rm-detail-text">
                                {room.defaultTherapistId
                                  ? getTherapistNameById(
                                      room.defaultTherapistId
                                    ) || "מטפל לא נמצא"
                                  : "חדר משותף · ללא מטפל קבוע"}
                              </span>
                            </div>
                            <div className="rm-detail">
                              <Users size={11} weight="fill" />
                              <span className="rm-detail-text">
                                {room.allowOthersToBook !== false
                                  ? "כל המטפלים יכולים לקבוע פגישות"
                                  : "רק המטפל המשויך והמנהל"}
                              </span>
                            </div>
                            {room.hasCustomAddress === true &&
                              room.customAddress && (
                                <div className="rm-detail">
                                  <MapPin size={11} weight="fill" />
                                  <span className="rm-detail-text">
                                    {room.customAddress}
                                  </span>
                                </div>
                              )}
                          </div>

                          {isAdmin && (
                            <div className="rm-card-footer">
                              <button
                                type="button"
                                className="rm-schedule-btn"
                                onClick={() => setScheduleRoom(room)}
                              >
                                <CalendarBlank size={14} weight="fill" />
                                לוח זמינות
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rm-empty">
                    <span className="rm-empty-icon">
                      <Door size={24} weight="fill" />
                    </span>
                    <h4>עדיין לא הוגדרו חדרים</h4>
                    <p>
                      הגדירו חדרי טיפול כדי לשבץ פגישות לפי חדרים ולנהל את
                      התפוסה ביומן
                    </p>
                    {isAdmin && (
                      <button
                        type="button"
                        className="rm-empty-cta"
                        onClick={() => handleOpenRoomModal()}
                      >
                        <Plus size={14} weight="bold" />
                        הוספת חדר ראשון
                      </button>
                    )}
                  </div>
                )}

                <div className="ck-adv-footnote">
                  <Info size={14} weight="fill" />
                  <span>
                    כאשר יש לפחות חדר אחד פעיל, יתאפשר שיוך פגישות לחדרים ביומן.
                    פגישות קיימות לא ישויכו אוטומטית לחדרים.
                  </span>
                </div>
              </section>
              ) : (
              <section className="ck-adv-card ck-multi-therapist-gate">
                <div className="ck-adv-card-header">
                  <div className="ck-adv-card-title">
                    <span className="ck-adv-card-icon">
                      <Door size={20} weight="fill" />
                    </span>
                    <div className="ck-adv-card-title-text">
                      <h2>ניהול חדרים</h2>
                      <p className="ck-adv-card-desc">
                        בקליניקה שלך עדיין לא הוגדרו חדרי טיפול
                      </p>
                    </div>
                  </div>
                </div>

                <p className="ck-mt-gate-lead">
                  יש לך יותר מחדר טיפול אחד? כך עובד ניהול החדרים:
                </p>
                <ul className="ck-mt-gate-list">
                  <li>
                    <span className="ck-mt-gate-li-icon">
                      <Door size={16} weight="fill" />
                    </span>
                    <span>
                      מגדירים לכל חדר שם, קיבולת, נגישות ושעות פעילות — כולל
                      חסימה אוטומטית בחגים וכתובת שונה מזו של הקליניקה.
                    </span>
                  </li>
                  <li>
                    <span className="ck-mt-gate-li-icon">
                      <CalendarCheck size={16} weight="fill" />
                    </span>
                    <span>
                      בקביעת פגישה יתווסף שדה &quot;חדר&quot;, והמערכת תבדוק
                      זמינות ותתריע על התנגשויות בין מטפלים באותו חדר.
                    </span>
                  </li>
                  <li>
                    <span className="ck-mt-gate-li-icon">
                      <MapPin size={16} weight="fill" />
                    </span>
                    <span>
                      ביומן תוכל לסנן לפי חדר ולראות את זמינותו, והמטופל יראה את
                      כתובת החדר בקישור אישור הפגישה.
                    </span>
                  </li>
                  <li>
                    <span className="ck-mt-gate-li-icon">
                      <CheckCircle size={16} weight="fill" />
                    </span>
                    <span>
                      ללא חדרים, הפגישות נקבעות כרגיל — פגישות קיימות לא ישויכו
                      לחדרים אוטומטית.
                    </span>
                  </li>
                </ul>
                <div className="ck-mt-gate-actions">
                  <CustomClinikitActionButton
                    icon={<Door size={16} weight="bold" />}
                    onClick={() => setShowRoomsManagement(true)}
                  >
                    אני רוצה לנהל חדרי טיפול בקליניקה
                  </CustomClinikitActionButton>
                </div>
              </section>
              )}
            </div>
          )}

          {/* ============================================
              TAB 2: CUSTOMIZATION
              ============================================ */}
          {activeTab === "customization" && (
            <div className="tab-content customization-tab">
              <div className="customization-grid">
                {/* Treatment Types */}
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon treatment-types">
                      <Stethoscope size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>סוגי טיפול</h3>
                      <p>הגדר סוגי טיפול, משך ותעריף. סוגים ציבוריים מוצגים גם בעמוד ההזמנה העצמית</p>
                    </div>
                    <button
                      className="add-item-btn"
                      onClick={() =>
                        handleOpenCategoryModal(null, "treatmentType")
                      }
                    >
                      <Plus size={16} weight="bold" />
                    </button>
                  </div>

                  <div className="card-content">
                    {isLoadingTreatmentTypes ? (
                      <ContentLoader size="sm" compact />
                    ) : treatmentTypes.length === 0 ? (
                      <div className="card-empty">
                        <p>לדוגמה: טיפול ראשוני, ייעוץ, אבחון...</p>
                      </div>
                    ) : (
                      <div className="items-list">
                        {treatmentTypes.map((category) => (
                          <div key={category.id} className="item-tag">
                            <span
                              className="item-color"
                              style={{ backgroundColor: category.color }}
                            />
                            <span className="item-name">{category.name}</span>
                            {category.duration > 0 && (
                              <span className="item-rate item-duration">
                                <Timer size={11} /> {category.duration} דק׳
                              </span>
                            )}
                            {category.rate != null && (
                              <span className="item-rate">₪{category.rate}</span>
                            )}
                            {category.isPublic === true && (
                              <span className="item-public-badge" title="מוצג בעמוד ההזמנה העצמית">
                                <Globe size={11} weight="fill" /> ציבורי
                              </span>
                            )}
                            {getPublicTypeWarnings(category).length > 0 && (
                              <span
                                className="item-warn"
                                title={getPublicTypeWarnings(category).join(" · ")}
                              >
                                <WarningCircle size={14} weight="fill" />
                              </span>
                            )}
                            <div className="item-actions">
                              <button
                                onClick={() =>
                                  handleOpenCategoryModal(category)
                                }
                                title="ערוך"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                className="delete"
                                onClick={() => handleDeleteCategory(category)}
                                title="מחק"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Treatment Tags */}
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon treatment-tags">
                      <Tag size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>תגיות תיעוד טיפולים</h3>
                      <p>תגיות לסיווג וסינון תיעודי טיפולים במערכת</p>
                    </div>
                    <button
                      className="add-item-btn"
                      onClick={() => handleOpenTreatmentTagModal(null)}
                    >
                      <Plus size={16} weight="bold" />
                    </button>
                  </div>

                  <div className="card-content">
                    {isLoadingTreatmentTags ? (
                      <ContentLoader size="sm" compact />
                    ) : treatmentTags.length === 0 ? (
                      <div className="card-empty">
                        <p>לדוגמה: דחוף, מעקב, סיכום...</p>
                      </div>
                    ) : (
                      <div className="items-list">
                        {treatmentTags.map((tag) => (
                          <div key={tag.id} className="item-tag">
                            <span
                              className="item-color"
                              style={{ backgroundColor: tag.color }}
                            />
                            <span className="item-name">{tag.name}</span>
                            <div className="item-actions">
                              <button
                                onClick={() => handleOpenTreatmentTagModal(tag)}
                                title="ערוך"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                className="delete"
                                onClick={() => handleDeleteTreatmentTag(tag)}
                                title="מחק"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Task Categories */}
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon tasks">
                      <ListChecks size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>קטגוריות משימות</h3>
                      <p>קטגוריות לארגון וסיווג משימות בקליניקה</p>
                    </div>
                    <button
                      className="add-item-btn"
                      onClick={() => handleOpenCategoryModal(null, "task")}
                    >
                      <Plus size={16} weight="bold" />
                    </button>
                  </div>

                  <div className="card-content">
                    {isLoadingTaskCategories ? (
                      <ContentLoader size="sm" compact />
                    ) : taskCategories.length === 0 ? (
                      <div className="card-empty">
                        <p>לדוגמה: מעקב טיפול, ביטוח, ניהול...</p>
                      </div>
                    ) : (
                      <div className="items-list">
                        {taskCategories.map((category) => (
                          <div key={category.id} className="item-tag">
                            <span
                              className="item-color"
                              style={{ backgroundColor: category.color }}
                            />
                            <span className="item-name">{category.name}</span>
                            <div className="item-actions">
                              <button
                                onClick={() =>
                                  handleOpenCategoryModal(category)
                                }
                                title="ערוך"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                className="delete"
                                onClick={() => handleDeleteCategory(category)}
                                title="מחק"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Reminder Categories */}
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon reminders">
                      <Note size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>קטגוריות תזכורות</h3>
                      <p>קטגוריות לארגון וסיווג תזכורות בקליניקה</p>
                    </div>
                    <button
                      className="add-item-btn"
                      onClick={() => handleOpenCategoryModal(null, "reminder")}
                    >
                      <Plus size={16} weight="bold" />
                    </button>
                  </div>

                  <div className="card-content">
                    {isLoadingReminderCategories ? (
                      <ContentLoader size="sm" compact />
                    ) : reminderCategories.length === 0 ? (
                      <div className="card-empty">
                        <p>לדוגמה: תשלומים, פגישות, מעקב...</p>
                      </div>
                    ) : (
                      <div className="items-list">
                        {reminderCategories.map((category) => (
                          <div key={category.id} className="item-tag">
                            <span
                              className="item-color"
                              style={{ backgroundColor: category.color }}
                            />
                            <span className="item-name">{category.name}</span>
                            <div className="item-actions">
                              <button
                                onClick={() =>
                                  handleOpenCategoryModal(category)
                                }
                                title="ערוך"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                className="delete"
                                onClick={() => handleDeleteCategory(category)}
                                title="מחק"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Calendar Start Hour */}
                <div className="customization-card calendar-hours-card">
                  <div className="card-header">
                    <div className="card-icon calendar-hours">
                      <Clock size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>שעות היומן</h3>
                      <p>מאיזו שעה יוצג היומן היומי והשבועי</p>
                    </div>
                  </div>

                  <div className="card-content calendar-hours-content">
                    <div className="calendar-hours-row">
                      <div className="pref-text">
                        <span className="pref-label">שעת פתיחת היומן</span>
                        <span className="pref-description">
                          השעה הראשונה שמוצגת ביומן — בדרך כלל שעת הפגישה הראשונה
                          ביום. היומן מוצג משעה זו ועד 23:00. ניתן לשנות בכל עת.
                        </span>
                      </div>
                      <div className="calendar-hours-select">
                        <HourSelect
                          value={getClinicCalendarStartHour(clinicProfile)}
                          options={CALENDAR_START_HOUR_OPTIONS}
                          onChange={handleCalendarStartHourChange}
                          disabled={isSavingCalendarStartHour}
                          ariaLabel="שעת פתיחת היומן"
                        />
                      </div>
                    </div>

                    {/* Calendar color mode — clinic-wide */}
                    <div className="calendar-hours-row">
                      <div className="pref-text">
                        <span className="pref-label">צביעת פגישות ביומן</span>
                        <span className="pref-description">
                          לפי סטטוס אישור — צבע הפגישה משקף אם המטופל אישר הגעה.
                          לפי סוג טיפול — צבע הפגישה הוא צבע סוג הטיפול, וסטטוס
                          האישור מוצג כתגית קטנה על הפגישה. זמן חסום ופגישות שבוטלו
                          נשארים תמיד באותם צבעים. חל על כל המטפלים במרפאה.
                        </span>
                      </div>
                      <div
                        className="cal-color-mode-seg"
                        role="radiogroup"
                        aria-label="צביעת פגישות ביומן"
                      >
                        {[
                          { key: CALENDAR_COLOR_MODES.STATUS, label: "לפי סטטוס אישור" },
                          { key: CALENDAR_COLOR_MODES.TREATMENT_TYPE, label: "לפי סוג טיפול" },
                        ].map((opt) => {
                          const checked = getCalendarColorMode(clinicProfile) === opt.key;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              role="radio"
                              aria-checked={checked}
                              className={`cal-color-mode-opt${checked ? " active" : ""}`}
                              disabled={isSavingCalendarColorMode}
                              onClick={() => handleCalendarColorModeChange(opt.key)}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Scheduling policies — multi-therapist clinics only */}
                    {!clinicProfile?.isSoloClinic && (
                      <>
                        <div className="calendar-hours-row">
                          <div className="pref-text">
                            <span className="pref-label">חסימת כפל תורים למטפל/ת</span>
                            <span className="pref-description">
                              כבוי — אשף הפגישות מציג אזהרה כשלמטפל/ת כבר יש תור
                              באותה שעה, וכל אחד/ת יכול/ה לאשר בכל זאת. פעיל —
                              החפיפה נחסמת ורק מנהל/ת יכול/ה לעקוף. טיפולים
                              קבוצתיים/זוגיים: השאר/י כבוי או השתמש/י בעקיפה.
                              כל עקיפה נרשמת ביומן הפעילות.
                            </span>
                          </div>
                          <label className="modern-toggle">
                            <input
                              type="checkbox"
                              checked={clinicProfile?.multiClinicSettings?.preventTherapistDoubleBooking === true}
                              disabled={savingSchedulingPolicy === "preventTherapistDoubleBooking"}
                              onChange={(e) =>
                                handleSchedulingPolicyChange("preventTherapistDoubleBooking", e.target.checked)
                              }
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>

                        <div className="calendar-hours-row">
                          <div className="pref-text">
                            <span className="pref-label">חסימת תורים בזמן היעדרות</span>
                            <span className="pref-description">
                              מטפלים מגדירים חופשות והיעדרויות בהגדרות היומן שלהם.
                              כבוי — אזהרה בלבד בקביעת תור בזמן היעדרות. פעיל —
                              חסימה, עם עקיפה למנהלים בלבד.
                            </span>
                          </div>
                          <label className="modern-toggle">
                            <input
                              type="checkbox"
                              checked={clinicProfile?.multiClinicSettings?.blockBookingOnAbsence === true}
                              disabled={savingSchedulingPolicy === "blockBookingOnAbsence"}
                              onChange={(e) =>
                                handleSchedulingPolicyChange("blockBookingOnAbsence", e.target.checked)
                              }
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>

                        {/* Leads v2 (leads_plan.md §4.1) — ONE clinic-level switch; absent ⇒ on */}
                        <div className="calendar-hours-row">
                          <div className="pref-text">
                            <span className="pref-label">מטפלים רואים ועורכים מתעניינים</span>
                            <span className="pref-description">
                              המתעניינים הם של הקליניקה. פעיל — כל הצוות רואה ועורך
                              את כל המתעניינים בארגז הכלים. כבוי — רק מנהלים;
                              שאר הצוות רואה בלשונית הודעה בלבד.
                            </span>
                          </div>
                          <label className="modern-toggle">
                            <input
                              type="checkbox"
                              checked={clinicProfile?.multiClinicSettings?.therapistsCanManageLeads !== false}
                              disabled={savingSchedulingPolicy === "therapistsCanManageLeads"}
                              onChange={(e) =>
                                handleSchedulingPolicyChange("therapistsCanManageLeads", e.target.checked)
                              }
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Display Preferences */}
                <div className="customization-card display-preferences-card">
                  <div className="card-header">
                    <div className="card-icon display-prefs">
                      <ToggleLeft size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>העדפות תצוגה במערכת</h3>
                      <p>שליטה בתכונות תצוגה ו-AI ברמת הקליניקה</p>
                    </div>
                  </div>

                  <div className="card-content display-prefs-content">
                    <div className="display-pref-row">
                      <div className="pref-info">
                        <div className="pref-icon">
                          <Sparkle size={18} weight="duotone" />
                        </div>
                        <div className="pref-text">
                          <span className="pref-label">סיכום מטופל AI</span>
                          <span className="pref-description">
                            יצירת סיכום AI אוטומטי לכל מטופל, כולל עדכון בעת נעילת דו"ח טיפול
                          </span>
                        </div>
                      </div>
                      <label className="modern-toggle">
                        <input
                          type="checkbox"
                          checked={clinicProfile?.enableAiPatientSummary === true}
                          onChange={async (e) => {
                            const newValue = e.target.checked;
                            try {
                              await updateClinic({
                                clinicId,
                                data: {
                                  enableAiPatientSummary: newValue,
                                },
                              }).unwrap();
                              dispatch(
                                setClinicProfile({
                                  ...clinicProfile,
                                  enableAiPatientSummary: newValue,
                                })
                              );
                              showSuccess(
                                newValue
                                  ? "סיכום מטופל AI הופעל בהצלחה"
                                  : "סיכום מטופל AI כובה"
                              );
                            } catch (err) {
                              console.error(
                                "[CK-SETTINGS] Error updating AI patient summary preference:",
                                err
                              );
                              showError("שגיאה בעדכון ההגדרה");
                            }
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    {!isNutritionClinic && (
                    <div className="display-pref-row">
                      <div className="pref-info">
                        <div className="pref-icon">
                          <UsersThree size={18} weight="duotone" />
                        </div>
                        <div className="pref-text">
                          <span className="pref-label">ניהול זוגות כמטופלים</span>
                          <span className="pref-description">
                            מאפשר ליצור מטופל מסוג זוג עם פרטי שני בני הזוג —
                            שני שמות, שני מספרי טלפון ובחירת מספר ראשי לקבלת
                            הודעות SMS. כיבוי האפשרות מסתיר את יצירת הזוגות
                            בלבד — מטופלים זוגיים קיימים נשמרים.
                          </span>
                        </div>
                      </div>
                      <label className="modern-toggle">
                        <input
                          type="checkbox"
                          checked={clinicProfile?.clinicManagesCouplesAsPatients === true}
                          onChange={async (e) => {
                            const newValue = e.target.checked === true;
                            try {
                              await updateClinic({
                                clinicId,
                                data: { clinicManagesCouplesAsPatients: newValue },
                              }).unwrap();
                              dispatch(
                                setClinicProfile({
                                  ...clinicProfile,
                                  clinicManagesCouplesAsPatients: newValue,
                                })
                              );
                              showSuccess(
                                newValue
                                  ? "ניהול זוגות כמטופלים הופעל בהצלחה"
                                  : "ניהול זוגות כמטופלים כובה — מטופלים זוגיים קיימים נשמרו"
                              );
                            } catch (err) {
                              console.error(
                                "[CK-5031] Error updating couples patients setting:",
                                {
                                  errorCode: "CK-5031",
                                  errorMessage: err?.message,
                                  clinicId,
                                  timestamp: new Date().toISOString(),
                                }
                              );
                              showError("שגיאה בעדכון ההגדרה");
                            }
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                    )}
                  </div>
                </div>

                {/* Punch Cards (כרטיסיות) */}
                <div className="customization-card punch-cards-card">
                  <div className="card-header">
                    <div className="card-icon punch-cards">
                      <Ticket size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>כרטיסיות ביקור</h3>
                      <p>מכירת חבילות מפגשים מראש למטופלים</p>
                    </div>
                  </div>

                  <div className="card-content display-prefs-content">
                    <div className="display-pref-row">
                      <div className="pref-info">
                        <div className="pref-icon">
                          <Ticket size={18} weight="duotone" />
                        </div>
                        <div className="pref-text">
                          <span className="pref-label">
                            המרפאה שלי עובדת עם כרטיסיות
                          </span>
                          <span className="pref-description">
                            כרטיסיה מאפשרת למכור למטופל חבילת מפגשים מראש ולנקב
                            מפגש בכל פגישה. לאחר ההפעלה תופיע לשונית "כרטיסיה"
                            בפרופיל המטופל ואפשרות "מתוך כרטיסיה" בקביעת פגישה.
                            כיבוי האפשרות מסתיר את התצוגה בלבד — כרטיסיות
                            קיימות נשמרות.
                          </span>
                        </div>
                      </div>
                      <label className="modern-toggle">
                        <input
                          type="checkbox"
                          checked={clinicProfile?.punchCardsEnabled === true}
                          onChange={async (e) => {
                            const newValue = e.target.checked === true;
                            try {
                              await updateClinic({
                                clinicId,
                                data: { punchCardsEnabled: newValue },
                              }).unwrap();
                              dispatch(
                                setClinicProfile({
                                  ...clinicProfile,
                                  punchCardsEnabled: newValue,
                                })
                              );
                              showSuccess(
                                newValue
                                  ? "כרטיסיות הופעלו בהצלחה"
                                  : "כרטיסיות כובו — הכרטיסיות הקיימות נשמרו"
                              );
                            } catch (err) {
                              console.error(
                                "[CK-5030] Error updating punch cards setting:",
                                {
                                  errorCode: "CK-5030",
                                  errorMessage: err?.message,
                                  clinicId,
                                  timestamp: new Date().toISOString(),
                                }
                              );
                              showError("שגיאה בעדכון ההגדרה");
                            }
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Temp Customers (לקוחות מזדמנים) */}
                <div className="customization-card temp-customers-card">
                  <div className="card-header">
                    <div className="card-icon temp-customers">
                      <UserPlus size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>לקוחות מזדמנים</h3>
                      <p>קביעת פגישה ללקוח חד־פעמי ללא פתיחת תיק מטופל</p>
                    </div>
                  </div>

                  <div className="card-content display-prefs-content">
                    <div className="display-pref-row">
                      <div className="pref-info">
                        <div className="pref-icon">
                          <UserPlus size={18} weight="duotone" />
                        </div>
                        <div className="pref-text">
                          <span className="pref-label">
                            המרפאה שלי מקבלת לקוחות מזדמנים
                          </span>
                          <span className="pref-description">
                            לאחר ההפעלה תופיע אפשרות "לקוח מזדמן" ברשימת
                            המטופלים בקביעת פגישה — מזינים שם ומספר נייד בלבד,
                            ללא פתיחת תיק מטופל. הלקוח מקבל SMS לאישור הגעה
                            ותזכורות כרגיל, והפגישה נכללת במעקב הכספי. בסיום
                            הפגישה לא יידרש סיכום טיפול.
                          </span>
                        </div>
                      </div>
                      <label className="modern-toggle">
                        <input
                          type="checkbox"
                          checked={clinicProfile?.tempCustomersEnabled === true}
                          onChange={async (e) => {
                            const newValue = e.target.checked === true;
                            try {
                              await updateClinic({
                                clinicId,
                                data: { tempCustomersEnabled: newValue },
                              }).unwrap();
                              dispatch(
                                setClinicProfile({
                                  ...clinicProfile,
                                  tempCustomersEnabled: newValue,
                                })
                              );
                              showSuccess(
                                newValue
                                  ? "לקוחות מזדמנים הופעלו בהצלחה"
                                  : "לקוחות מזדמנים כובו — פגישות קיימות נשמרו"
                              );
                            } catch (err) {
                              console.error(
                                "[CK-5050] Error updating temp customers setting:",
                                {
                                  errorCode: "CK-5050",
                                  errorMessage: err?.message,
                                  clinicId,
                                  timestamp: new Date().toISOString(),
                                }
                              );
                              showError("שגיאה בעדכון ההגדרה");
                            }
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Online Video Meetings (100ms) */}
                <div className="customization-card video-meetings-card">
                  <div className="card-header">
                    <div className="card-icon video-meetings">
                      <VideoCamera size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>פגישות וידאו אונליין</h3>
                      <p>קיום פגישות טיפול מרחוק בשיחת וידאו מתוך המערכת</p>
                    </div>
                  </div>

                  <div className="card-content display-prefs-content">
                    <div className="display-pref-row">
                      <div className="pref-info">
                        <div className="pref-icon">
                          <VideoCamera size={18} weight="duotone" />
                        </div>
                        <div className="pref-text">
                          <span className="pref-label">
                            המרפאה שלי מקיימת פגישות וידאו אונליין
                          </span>
                          <span className="pref-description">
                            לאחר ההפעלה תופיע אפשרות "פגישת וידאו אונליין"
                            בקביעת פגישה. המטופל מקבל קישור להצטרפות לשיחה
                            יחד עם SMS אישור ההגעה — ללא התקנה וללא הרשמה,
                            ישירות מהדפדפן. המטפל מצטרף לשיחה מתוך הפגישה
                            ביומן. המכסה החודשית היא 5 שעות וידאו לקליניקה.
                          </span>
                        </div>
                      </div>
                      <label className="modern-toggle">
                        <input
                          type="checkbox"
                          checked={clinicProfile?.onlineMeetingsEnabled === true}
                          onChange={async (e) => {
                            const newValue = e.target.checked === true;
                            try {
                              await updateClinic({
                                clinicId,
                                data: { onlineMeetingsEnabled: newValue },
                              }).unwrap();
                              dispatch(
                                setClinicProfile({
                                  ...clinicProfile,
                                  onlineMeetingsEnabled: newValue,
                                })
                              );
                              showSuccess(
                                newValue
                                  ? "פגישות וידאו הופעלו בהצלחה"
                                  : "פגישות וידאו כובו"
                              );
                            } catch (err) {
                              console.error(
                                "[CK-5051] Error updating online meetings setting:",
                                {
                                  errorCode: "CK-5051",
                                  errorMessage: err?.message,
                                  clinicId,
                                  timestamp: new Date().toISOString(),
                                }
                              );
                              showError("שגיאה בעדכון ההגדרה");
                            }
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* English-speaking patients */}
                <div className="customization-card">
                  <div className="card-header">
                    <div className="card-icon">
                      <Translate size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>מטופלים דוברי אנגלית</h3>
                      <p>תקשורת באנגלית עם מטופלים שאינם דוברי עברית</p>
                    </div>
                  </div>

                  <div className="card-content display-prefs-content">
                    <div className="display-pref-row">
                      <div className="pref-info">
                        <div className="pref-icon">
                          <Translate size={18} weight="duotone" />
                        </div>
                        <div className="pref-text">
                          <span className="pref-label">
                            אני עובד/ת עם מטופלים דוברי אנגלית
                          </span>
                          <span className="pref-description">
                            לאחר ההפעלה תופיע בכרטיס המטופל אפשרות לסמן "שפת
                            תקשורת: אנגלית". מטופל שסומן יקבל את הודעות ה-SMS
                            שלו ואת דף אישור ההגעה באנגלית. שאר המטופלים
                            ממשיכים לקבל הכל בעברית.
                          </span>
                        </div>
                      </div>
                      <label className="modern-toggle">
                        <input
                          type="checkbox"
                          checked={
                            clinicProfile?.englishCommunicationEnabled === true
                          }
                          onChange={async (e) => {
                            const newValue = e.target.checked === true;
                            try {
                              await updateClinic({
                                clinicId,
                                data: { englishCommunicationEnabled: newValue },
                              }).unwrap();
                              dispatch(
                                setClinicProfile({
                                  ...clinicProfile,
                                  englishCommunicationEnabled: newValue,
                                })
                              );
                              showSuccess(
                                newValue
                                  ? "תקשורת באנגלית הופעלה בהצלחה"
                                  : "תקשורת באנגלית כובתה"
                              );
                            } catch (err) {
                              console.error(
                                "[CK-5070] Error updating English communication setting:",
                                {
                                  errorCode: "CK-5070",
                                  errorMessage: err?.message,
                                  clinicId,
                                  timestamp: new Date().toISOString(),
                                }
                              );
                              showError("שגיאה בעדכון ההגדרה");
                            }
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Games/Toys Management - Speech Therapist Only */}
                {clinicProfile?.type === "speech_therapist" && <div className="customization-card games-card">
                  <div className="card-header">
                    <div className="card-icon games">
                      <GameController size={22} weight="duotone" />
                    </div>
                    <div className="card-title">
                      <h3>ניהול משחקים וצעצועים</h3>
                      <p>הגדר משחקים לבחירה בסיכום טיפול</p>
                    </div>
                    <button
                      className="add-item-btn"
                      onClick={() => handleOpenGameModal(null)}
                    >
                      <Plus size={16} weight="bold" />
                    </button>
                  </div>

                  <div className="card-content">
                    {isLoadingGames ? (
                      <ContentLoader size="sm" compact />
                    ) : clinicGames.length === 0 ? (
                      <div className="card-empty">
                        <p>לדוגמה: קוביות, פאזל, משחק זיכרון</p>
                      </div>
                    ) : (
                      <div className="items-list games-list">
                        {clinicGames.map((game) => {
                          const gameGoals = getGoalsByIds(game.goalIds || []);
                          const ageRange =
                            game.minAge || game.maxAge
                              ? `${game.minAge || "?"}-${game.maxAge || "?"}`
                              : null;
                          return (
                            <div key={game.id} className="game-item">
                              <div className="game-item-main">
                                <span className="game-name">{game.name}</span>
                                {ageRange && (
                                  <span className="game-age-badge">
                                    גילאים {ageRange}
                                  </span>
                                )}
                              </div>
                              {gameGoals.length > 0 && (
                                <div className="game-goals-preview">
                                  {gameGoals.slice(0, 2).map((goal) => (
                                    <span
                                      key={goal.id}
                                      className="goal-mini-badge"
                                      style={{
                                        backgroundColor:
                                          goal.categoryColor + "20",
                                        color: goal.categoryColor,
                                      }}
                                    >
                                      {goal.name.length > 20
                                        ? goal.name.substring(0, 20) + "..."
                                        : goal.name}
                                    </span>
                                  ))}
                                  {gameGoals.length > 2 && (
                                    <span className="goals-more">
                                      +{gameGoals.length - 2}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="item-actions">
                                <button
                                  onClick={() => handleOpenGameModal(game)}
                                  title="ערוך"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  className="delete"
                                  onClick={() => handleDeleteGame(game)}
                                  title="מחק"
                                >
                                  <Trash size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>}
              </div>
            </div>
          )}

          {/* ============================================
              TAB 3: INTEGRATIONS
              ============================================ */}
          {activeTab === "integrations" && (
            <div className="tab-content integrations-tab-modern">
              {/* Morning Integration Section */}
              <div className="morning-integration-card">
                <div className="morning-card-header">
                  <div className="morning-logo-wrapper">
                    <img
                      src={morningLogo}
                      alt="Morning"
                      className="morning-logo-img"
                    />
                  </div>
                  <div className="morning-header-content">
                    <div className="morning-title-row">
                      <h3>מורנינג (חשבונית ירוקה)</h3>
                      {clinicProfile?.isMorningPaymentSystemIntegrated ? (
                        <span className="morning-status-badge connected">
                          <PlugsConnected size={14} weight="bold" />
                          מחובר
                        </span>
                      ) : (
                        <span className="morning-status-badge disconnected">
                          <Plugs size={14} weight="bold" />
                          לא מחובר
                        </span>
                      )}
                    </div>
                    <p>הפקת חשבוניות מס וקבלות ישירות מ-CliniKit</p>
                  </div>
                </div>

                <div className="morning-card-body">
                  {clinicProfile?.isMorningPaymentSystemIntegrated ? (
                    <>
                      {/* Connected State */}
                      <div className="morning-connected-info">
                        <div className="morning-info-grid">
                          <div className="morning-info-item">
                            <span className="info-label">שם העסק</span>
                            <span className="info-value">
                              {clinicProfile?.morningIntegration
                                ?.businessName || clinicProfile?.name}
                            </span>
                          </div>
                          <div className="morning-info-item">
                            <span className="info-label">מחובר מתאריך</span>
                            <span className="info-value">
                              {clinicProfile?.morningIntegration?.connectedAt
                                ? new Date(
                                    clinicProfile.morningIntegration.connectedAt
                                  ).toLocaleDateString("he-IL")
                                : "לא ידוע"}
                            </span>
                          </div>
                          <div className="morning-info-item">
                            <span className="info-label">סוג מסמך ברירת מחדל</span>
                            <span className="info-value">
                              {MORNING_DOCUMENT_LABELS[
                                resolveClinicDefaultDocumentType(clinicProfile?.morningIntegration)
                              ] || "חשבונית מס / קבלה"}
                            </span>
                          </div>
                          <div className="morning-info-item">
                            <span className="info-label">סוג עוסק</span>
                            <span className="info-value">
                              {MORNING_BUSINESS_TYPE_LABELS[
                                clinicProfile?.morningIntegration?.businessType
                              ] || "לא ידוע"}
                              {(clinicProfile?.morningIntegration?.exemption === true ||
                                clinicProfile?.morningIntegration?.businessType === "osekPatur") &&
                                " · פטור ממע״מ"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Therapist Permissions Toggle */}
                      <div className="morning-permissions-section">
                        <div className="permissions-toggle-row">
                          <div className="toggle-label-section">
                            <div className="toggle-label-with-icon">
                              <UsersThree
                                size={20}
                                weight="fill"
                                className="toggle-icon"
                              />
                              <span className="toggle-label">
                                אפשר למטפלים להפיק חשבוניות
                              </span>
                            </div>
                            <div className="toggle-info-tooltip">
                              <Info
                                size={16}
                                weight="fill"
                                className="info-icon"
                              />
                              <div className="tooltip-content">
                                כאשר מופעל, כל המטפלים בקליניקה יוכלו להפיק
                                חשבוניות בשם העסק שלך. כאשר כבוי, רק בעל
                                הקליניקה יוכל להפיק חשבוניות.
                              </div>
                            </div>
                          </div>
                          <label className="modern-toggle">
                            <input
                              type="checkbox"
                              checked={
                                clinicProfile?.morningIntegration
                                  ?.allowTherapistsToCreateInvoices === true
                              }
                              onChange={async (e) => {
                                const newValue = e.target.checked;
                                try {
                                  // Server-side (updateMorningSettings) — same path as the settings modal
                                  const updateMorningSettings = httpsCallable(
                                    functions,
                                    "updateMorningSettings"
                                  );
                                  const res = await updateMorningSettings({
                                    clinicId,
                                    settings: { allowTherapistsToCreateInvoices: newValue },
                                  });
                                  if (!res.data?.success) {
                                    throw new Error(res.data?.error || "update failed");
                                  }
                                  dispatch(
                                    setClinicProfile({
                                      ...clinicProfile,
                                      morningIntegration:
                                        res.data.morningIntegration || {
                                          ...clinicProfile.morningIntegration,
                                          allowTherapistsToCreateInvoices: newValue,
                                        },
                                    })
                                  );
                                  showSuccess(
                                    newValue
                                      ? "מטפלים יכולים כעת להפיק חשבוניות"
                                      : "רק בעל הקליניקה יכול להפיק חשבוניות"
                                  );
                                } catch (err) {
                                  console.error("[CK-8814] Error updating Morning permissions:", {
                                    errorCode: "CK-8814",
                                    errorMessage: err.message,
                                    timestamp: new Date().toISOString(),
                                  });
                                  showError("שגיאה בעדכון ההרשאות");
                                }
                              }}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        <span className="permissions-hint">
                          {clinicProfile?.morningIntegration
                            ?.allowTherapistsToCreateInvoices
                            ? "כל המטפלים בקליניקה יכולים להפיק חשבוניות"
                            : "רק בעל הקליניקה יכול להפיק חשבוניות"}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="morning-actions">
                        <button
                          className="morning-action-btn secondary"
                          onClick={() => setShowMorningSettings(true)}
                        >
                          <Pencil size={16} />
                          <span>הגדרות הפקה</span>
                        </button>
                        <button
                          className="morning-action-btn secondary"
                          onClick={() => setShowMorningWizard(true)}
                          title="החלפת מפתח API"
                        >
                          <Key size={16} />
                          <span>עדכן פרטי חיבור</span>
                        </button>
                        <button
                          className="morning-action-btn secondary danger"
                          onClick={() => setShowMorningDisconnect(true)}
                        >
                          <Plugs size={16} />
                          <span>ניתוק</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Not Connected State */}
                      <div className="morning-benefits">
                        <div className="benefit-item">
                          <CheckCircle
                            size={18}
                            weight="fill"
                            className="benefit-icon"
                          />
                          <span>הפקת חשבוניות מס אוטומטית</span>
                        </div>
                        <div className="benefit-item">
                          <CheckCircle
                            size={18}
                            weight="fill"
                            className="benefit-icon"
                          />
                          <span>שליחה ישירה למטופל באימייל</span>
                        </div>
                        <div className="benefit-item">
                          <CheckCircle
                            size={18}
                            weight="fill"
                            className="benefit-icon"
                          />
                          <span>מעקב מסמכים מרוכז</span>
                        </div>
                        <div className="benefit-item">
                          <CheckCircle
                            size={18}
                            weight="fill"
                            className="benefit-icon"
                          />
                          <span>התאמה לדרישות רשות המסים</span>
                        </div>
                      </div>

                      <CustomClinikitButton
                        variant="primary"
                        onClick={() => setShowMorningWizard(true)}
                        className="morning-connect-btn"
                      >
                        <Plugs size={18} weight="bold" />
                        <span>חבר את מורנינג</span>
                      </CustomClinikitButton>
                    </>
                  )}
                </div>
              </div>

              {/* Digital Invoice Integration */}
              <div className="morning-integration-card">
                <div className="morning-card-header">
                  <div className="morning-header-icon-container" style={{ background: "linear-gradient(135deg, #6366f1, #4f46e5)" }}>
                    <Receipt size={24} weight="duotone" />
                  </div>
                  <div className="morning-header-info">
                    <div className="morning-header-row">
                      <h3>חשבונית דיגיטלית</h3>
                      {isDIIntegrated ? (
                        <span className="morning-status-badge connected">
                          <PlugsConnected size={14} weight="bold" />
                          מחובר
                        </span>
                      ) : (
                        <span className="morning-status-badge disconnected">
                          <Plugs size={14} weight="bold" />
                          לא מחובר
                        </span>
                      )}
                    </div>
                    <p>הפקת חשבוניות מס וקבלות ישירות מ-CliniKit</p>
                  </div>
                </div>

                <div className="morning-card-body">
                  {isDIIntegrated ? (
                    <>
                      <div className="morning-connected-info">
                        <div className="morning-info-grid">
                          <div className="morning-info-item">
                            <span className="info-label">שם העסק</span>
                            <span className="info-value">
                              {diIntegrationInfo?.companyName || clinicProfile?.name}
                            </span>
                          </div>
                          <div className="morning-info-item">
                            <span className="info-label">מחובר מתאריך</span>
                            <span className="info-value">
                              {diIntegrationInfo?.connectedAt
                                ? new Date(diIntegrationInfo.connectedAt).toLocaleDateString("he-IL")
                                : "לא ידוע"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="morning-actions">
                        <button
                          className="morning-action-btn secondary"
                          onClick={() => setShowDIWizard(true)}
                        >
                          <Pencil size={16} />
                          <span>עדכן הגדרות</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="morning-benefits">
                        <div className="benefit-item">
                          <CheckCircle size={18} weight="fill" className="benefit-icon" />
                          <span>הפקת חשבוניות מס אוטומטית</span>
                        </div>
                        <div className="benefit-item">
                          <CheckCircle size={18} weight="fill" className="benefit-icon" />
                          <span>שליחה ישירה למטופל באימייל</span>
                        </div>
                        <div className="benefit-item">
                          <CheckCircle size={18} weight="fill" className="benefit-icon" />
                          <span>מעקב מסמכים מרוכז</span>
                        </div>
                        <div className="benefit-item">
                          <CheckCircle size={18} weight="fill" className="benefit-icon" />
                          <span>התאמה לדרישות רשות המסים</span>
                        </div>
                      </div>

                      <CustomClinikitButton
                        variant="primary"
                        onClick={() => setShowDIWizard(true)}
                        className="morning-connect-btn"
                      >
                        <Plugs size={18} weight="bold" />
                        <span>התחל להנפיק עם חשבונית דיגיטלית</span>
                      </CustomClinikitButton>
                    </>
                  )}
                </div>
              </div>

              {/* Grow payments (grow_integration.md) — self-contained card: wizard / settings / disconnect modals live inside */}
              <GrowIntegrationCard />

              {/* Leads v2 (leads_plan.md §8) — inbound webhook + API keys; self-contained card */}
              <LeadsWebhookCard />

              {/* Coming Soon */}
              <div className="coming-soon-card-modern">
                <Lightbulb
                  size={28}
                  weight="duotone"
                  className="coming-soon-icon"
                />
                <div className="coming-soon-content">
                  <h4>אינטגרציות נוספות בקרוב</h4>
                  <p>אנחנו עובדים על חיבורים נוספים למערכות סליקה ותשלום</p>
                </div>
              </div>
            </div>
          )}

          {/* ============================================
              TAB 4: MORNING DOCUMENTS MANAGEMENT
              ============================================ */}
          {/* Morning tab content */}
          {activeTab === "morning" && (
            <div className="tab-content morning-tab-content">
              {isMorningInitialLoading ? (
                <ContentLoader fill message="טוען נתונים ממורנינג..." />
              ) : (
                <div className="morning-page-embedded">
                  {/* Header */}
                  <div className="morning-page-header">
                    <div className="header-title">
                      <img src={morningLogo} alt="Morning" className="morning-logo-icon" />
                      <div>
                        <h1>ניהול מורנינג</h1>
                        <span className="connection-status">
                          <CheckCircle size={14} weight="fill" />
                          מחובר | {morningIntegrationInfo?.businessName || "עסק"}
                        </span>
                      </div>
                    </div>
                    <CustomClinikitButtonLight onClick={() => setShowCreateInvoiceModal(true)}>
                      <Plus size={20} weight="bold" />
                      <span>הפק חשבונית</span>
                    </CustomClinikitButtonLight>
                  </div>

                  {/* Stats Card */}
                  <div className="morning-stats-single">
                    <div className="stat-card-large">
                      <div className="stat-icon documents">
                        <FileText size={32} weight="duotone" />
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{morningTotalCount}</span>
                        <span className="stat-label">סה״כ מסמכים במורנינג</span>
                      </div>
                      <div className="stat-actions">
                        <button
                          className="stat-link"
                          onClick={() => window.open("https://app.greeninvoice.co.il/reports", "_blank")}
                        >
                          <ChartLineUp size={18} />
                          <span>צפה בדוחות ונתונים כספיים</span>
                          <ArrowSquareOut size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Documents List */}
                  <div className="documents-section">
                    <div className="section-headerrr">
                      <h2>מסמכים</h2>
                      <div className="section-actions">
                        {/* Search Box */}
                        <div className="search-box">
                          <MagnifyingGlass size={18} />
                          <input
                            type="text"
                            placeholder="חיפוש לפי שם לקוח..."
                            value={morningSearchQuery}
                            onChange={(e) => setMorningSearchQuery(e.target.value)}
                            onKeyDown={handleMorningSearchKeyDown}
                          />
                          {morningSearchQuery && (
                            <button
                              className="clear-search"
                              onClick={() => setMorningSearchQuery("")}
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>

                        {/* Date Filters */}
                        <div className="date-filter">
                          <DatePicker
                            value={morningDateFrom}
                            onChange={(e) => setMorningDateFrom(e.target.value)}
                            placeholder="מתאריך"
                          />
                          <span className="date-separator">-</span>
                          <DatePicker
                            value={morningDateTo}
                            onChange={(e) => setMorningDateTo(e.target.value)}
                            placeholder="עד תאריך"
                          />
                        </div>

                        {/* Type Filter */}
                        <div className="filter-select">
                          <Funnel size={18} />
                          <select
                            value={morningFilterType}
                            onChange={(e) => setMorningFilterType(e.target.value)}
                          >
                            <option value="all">כל הסוגים</option>
                            {getDocumentTypeOptions().map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Search Button */}
                        <button className="btn-search" onClick={handleMorningSearch}>
                          <MagnifyingGlass size={18} />
                          <span>חפש</span>
                        </button>

                        {/* Refresh Button */}
                        <button
                          className="btn-refresh"
                          onClick={() => loadMorningDocuments(morningCurrentPage)}
                          disabled={isMorningLoading}
                          title="רענן רשימה"
                        >
                          <ArrowsClockwise
                            size={18}
                            className={isMorningLoading ? "spinning" : ""}
                          />
                        </button>

                        {/* Clear Filters */}
                        {(morningSearchQuery || morningFilterType !== "all" || morningDateFrom || morningDateTo) && (
                          <button
                            className="btn-clear-filters"
                            onClick={handleMorningClearFilters}
                          >
                            <X size={16} />
                            <span>נקה סינון</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Error State */}
                    {morningLoadError && (
                      <div className="error-state">
                        <WarningCircle size={32} weight="fill" />
                        <span>{morningLoadError}</span>
                        <button onClick={() => loadMorningDocuments(morningCurrentPage)}>נסה שוב</button>
                      </div>
                    )}

                    {/* Loading State */}
                    {isMorningLoading ? (
                      <ContentLoader message="טוען מסמכים..." />
                    ) : morningDocuments.length === 0 ? (
                      <div className="empty-state">
                        <FileText size={48} weight="duotone" />
                        <h3>אין מסמכים להצגה</h3>
                        <p>
                          {morningSearchQuery || morningFilterType !== "all" || morningDateFrom || morningDateTo
                            ? "לא נמצאו מסמכים התואמים לחיפוש"
                            : "הפק את החשבונית הראשונה שלך"}
                        </p>
                        {!(morningSearchQuery || morningFilterType !== "all" || morningDateFrom || morningDateTo) && (
                          <button
                            className="btn-primary"
                            onClick={() => setShowCreateInvoiceModal(true)}
                          >
                            <Plus size={18} />
                            <span>הפק חשבונית</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="documents-table-wrapper">
                          <table className="documents-table">
                            <thead>
                              <tr>
                                <th>מספר</th>
                                <th>סוג</th>
                                <th>תאריך</th>
                                <th>לקוח</th>
                                <th>סכום</th>
                                <th>תשלום</th>
                                <th>פעולות</th>
                              </tr>
                            </thead>
                            <tbody>
                              {morningDocuments.map((doc) => (
                                <tr key={doc.id}>
                                  <td className="doc-number">{doc.number}</td>
                                  <td>
                                    <span className="doc-type-badge">
                                      {MORNING_DOCUMENT_LABELS[doc.type] || "מסמך"}
                                    </span>
                                  </td>
                                  <td className="doc-date">{formatMorningDate(doc.createdAt)}</td>
                                  <td className="doc-patient">
                                    <User size={16} />
                                    <span>{doc.clientName || "-"}</span>
                                  </td>
                                  <td className="doc-amount">
                                    {formatCurrency(doc.totalAmount)}
                                  </td>
                                  <td>
                                    <span className="payment-badge">
                                      {MORNING_PAYMENT_LABELS[doc.paymentType] || "אחר"}
                                    </span>
                                  </td>
                                  <td className="doc-actions">
                                    <button
                                      className="action-btn view"
                                      title="צפייה במסמך"
                                      onClick={() => handleViewMorningDocument(doc)}
                                    >
                                      <Eye size={18} />
                                    </button>
                                    <button
                                      className="action-btn external"
                                      title="פתח במורנינג"
                                      onClick={handleOpenMorning}
                                    >
                                      <ArrowSquareOut size={18} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {morningTotalPages > 1 && (
                          <div className="pagination">
                            <button
                              className="pagination-btn"
                              onClick={() => handleMorningPageChange(morningCurrentPage - 1)}
                              disabled={morningCurrentPage === 1 || isMorningLoading}
                            >
                              <CaretRight size={18} />
                            </button>

                            <div className="pagination-info">
                              <span>
                                עמוד {morningCurrentPage} מתוך {morningTotalPages}
                              </span>
                              <span className="pagination-total">
                                ({morningTotalCount} מסמכים)
                              </span>
                            </div>

                            <button
                              className="pagination-btn"
                              onClick={() => handleMorningPageChange(morningCurrentPage + 1)}
                              disabled={morningCurrentPage === morningTotalPages || isMorningLoading}
                            >
                              <CaretLeft size={18} />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="quick-actions-section">
                    <h3>פעולות מהירות</h3>
                    <div className="quick-actions-grid-morning">
                      <button className="quick-action-card" onClick={handleOpenMorning}>
                        <ArrowSquareOut size={24} />
                        <span>פתח את מורנינג</span>
                      </button>
                      <button
                        className="quick-action-card"
                        onClick={() => window.open("https://app.greeninvoice.co.il/reports", "_blank")}
                      >
                        <ChartLineUp size={24} />
                        <span>צפה בדוחות</span>
                      </button>
                      <button
                        className="quick-action-card"
                        onClick={() => setActiveTab("integrations")}
                      >
                        <Plugs size={24} />
                        <span>הגדרות חיבור</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============================================
              TAB: DIGITAL INVOICE DOCUMENTS MANAGEMENT
              ============================================ */}
          {activeTab === "digitalInvoice" && (
            <div className="tab-content morning-tab-content">
              {isDIInitialLoading ? (
                <ContentLoader fill message="טוען נתונים מחשבונית דיגיטלית..." />
              ) : (
                <div className="morning-page-embedded">
                  {/* Header */}
                  <div className="morning-page-header">
                    <div className="header-title">
                      <div style={{
                        width: 40, height: 40, borderRadius: 7,
                        background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                        color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Receipt size={22} weight="duotone" />
                      </div>
                      <div>
                        <h1>ניהול חשבוניות</h1>
                        <span className="connection-status">
                          <CheckCircle size={14} weight="fill" />
                          מחובר | {diIntegrationInfo?.companyName || "עסק"}
                        </span>
                      </div>
                    </div>
                    <div className="header-actions">
                      <CustomClinikitButton
                        variant="primary"
                        onClick={() => setShowDICreateModal(true)}
                      >
                        <Receipt size={16} weight="bold" />
                        <span>הפק חשבונית</span>
                      </CustomClinikitButton>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="morning-filters">
                    <div className="morning-search-row">
                      <input
                        type="text"
                        className="morning-search-input"
                        placeholder="חפש לפי שם לקוח..."
                        value={diSearchQuery}
                        onChange={(e) => setDISearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setDICurrentPage(1);
                            handleLoadDIDocuments(1);
                          }
                        }}
                      />
                      <select
                        className="morning-filter-select"
                        value={diFilterType}
                        onChange={(e) => {
                          setDIFilterType(Number(e.target.value));
                          setDICurrentPage(1);
                        }}
                      >
                        {getDIDocTypeOptions().map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="morning-date-row">
                      <DatePicker
                        className="morning-date-input"
                        value={diDateFrom}
                        onChange={(e) => setDIDateFrom(e.target.value)}
                        placeholder="מתאריך"
                      />
                      <DatePicker
                        className="morning-date-input"
                        value={diDateTo}
                        onChange={(e) => setDIDateTo(e.target.value)}
                        placeholder="עד תאריך"
                      />
                      <button
                        className="morning-search-btn"
                        onClick={() => {
                          setDICurrentPage(1);
                          handleLoadDIDocuments(1);
                        }}
                      >
                        חפש
                      </button>
                      <button
                        className="morning-clear-btn"
                        onClick={() => {
                          setDISearchQuery("");
                          setDIFilterType(0);
                          setDIDateFrom("");
                          setDIDateTo("");
                          setDICurrentPage(1);
                          handleLoadDIDocuments(1, true);
                        }}
                      >
                        נקה
                      </button>
                    </div>
                  </div>

                  {/* Documents Table */}
                  {isDILoading ? (
                    <ContentLoader size="sm" compact message="טוען..." />
                  ) : diLoadError ? (
                    <div className="morning-error">
                      <WarningCircle size={24} weight="fill" />
                      <span>{diLoadError}</span>
                      <button onClick={() => handleLoadDIDocuments(diCurrentPage)}>נסה שוב</button>
                    </div>
                  ) : diDocuments.length === 0 ? (
                    <div className="morning-empty">
                      <Receipt size={48} weight="thin" />
                      <h3>אין מסמכים</h3>
                      <p>לא נמצאו מסמכים בטווח התאריכים שנבחר</p>
                      <CustomClinikitButton
                        variant="primary"
                        onClick={() => setShowDICreateModal(true)}
                      >
                        <Receipt size={16} weight="bold" />
                        <span>הפק חשבונית ראשונה</span>
                      </CustomClinikitButton>
                    </div>
                  ) : (
                    <>
                      <div className="morning-documents-table">
                        <table>
                          <thead>
                            <tr>
                              <th>מספר</th>
                              <th>תאריך</th>
                              <th>לקוח</th>
                              <th>סוג</th>
                              <th>סכום</th>
                              <th>פעולות</th>
                            </tr>
                          </thead>
                          <tbody>
                            {diDocuments.map((doc) => (
                              <tr key={doc.id}>
                                <td className="doc-number">{doc.number || "-"}</td>
                                <td>
                                  {doc.dateCreated
                                    ? new Date(doc.dateCreated).toLocaleDateString("he-IL")
                                    : "-"}
                                </td>
                                <td>{doc.clientName || "-"}</td>
                                <td>
                                  <span className="doc-type-badge">
                                    {DI_DOCUMENT_LABELS[doc.type] || `סוג ${doc.type}`}
                                  </span>
                                </td>
                                <td className="doc-amount">
                                  {diFormatCurrency(doc.total)}
                                </td>
                                <td className="doc-actions">
                                  {doc.invoiceUrl && (
                                    <a
                                      href={doc.invoiceUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="doc-action-link"
                                      title="צפה"
                                    >
                                      <Eye size={16} />
                                    </a>
                                  )}
                                  {doc.pdfUrl && (
                                    <a
                                      href={doc.pdfUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="doc-action-link"
                                      title="הורד PDF"
                                    >
                                      <FileText size={16} />
                                    </a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {diTotalPages > 1 && (
                        <div className="morning-pagination">
                          <button
                            disabled={diCurrentPage <= 1}
                            onClick={() => {
                              const newPage = diCurrentPage - 1;
                              setDICurrentPage(newPage);
                              handleLoadDIDocuments(newPage);
                            }}
                          >
                            הקודם
                          </button>
                          <span>
                            עמוד {diCurrentPage} מתוך {diTotalPages}
                          </span>
                          <button
                            disabled={diCurrentPage >= diTotalPages}
                            onClick={() => {
                              const newPage = diCurrentPage + 1;
                              setDICurrentPage(newPage);
                              handleLoadDIDocuments(newPage);
                            }}
                          >
                            הבא
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ============================================
              TAB 5: ACTIVITY LOGS
              ============================================ */}
          {activeTab === "activity" && (
            <div className="tab-content activity-tab-content">
              <div className="admin-activity-logs-page embedded">
                <div className="activity-logs-header">
                  <div className="header-title">
                    <h1>
                      <ClockCounterClockwise size={20} weight="bold" />
                      <span>תיעודי פעילות</span>
                    </h1>
                    <p className="subtitle">
                      צפייה בכל הפעולות שבוצעו במערכת על ידי המטפלים
                    </p>
                  </div>
                </div>

                <div className="activity-logs-filters">
                  {/* Therapist Filter */}
                  <div className="filter-wrapper">
                    <User size={18} weight="regular" className="filter-icon" />
                    <select
                      value={activitySelectedTherapist}
                      onChange={handleActivityTherapistChange}
                      className="filter-select"
                    >
                      <option value="all">כל המטפלים</option>
                      {therapists.map((therapist) => (
                        <option key={therapist.uid || therapist.id} value={therapist.uid || therapist.id}>
                          {therapist.firstName} {therapist.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action Filter */}
                  <div className="filter-wrapper">
                    <Funnel size={18} weight="regular" className="filter-icon" />
                    <select
                      value={activitySelectedAction}
                      onChange={handleActivityActionChange}
                      className="filter-select"
                    >
                      {ACTIVITY_ACTION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Range Filters */}
                  <div className="filter-wrapper date-filter">
                    <DatePicker
                      value={activityStartDate}
                      onChange={(e) => handleActivityStartDateChange(e)}
                      placeholder="מתאריך"
                    />
                  </div>

                  <div className="filter-wrapper date-filter">
                    <DatePicker
                      value={activityEndDate}
                      onChange={(e) => handleActivityEndDateChange(e)}
                      placeholder="עד תאריך"
                    />
                  </div>

                  {/* Clear Filters */}
                  {hasActiveActivityFilters && (
                    <button className="clear-filters-btn" onClick={clearActivityFilters}>
                      נקה סינון
                    </button>
                  )}
                </div>

                {/* Loading State */}
                {isLoadingActivityLogs && (
                  <ContentLoader message="טוען תיעודי פעילות..." />
                )}

                {/* Empty State */}
                {!isLoadingActivityLogs && activityLogs.length === 0 && (
                  <div className="empty-state">
                    <ClockCounterClockwise size={48} weight="light" />
                    <h3>אין תיעודי פעילות</h3>
                    <p>
                      {hasActiveActivityFilters
                        ? "לא נמצאו תיעודים התואמים את הסינון"
                        : "עדיין לא בוצעו פעולות במערכת"}
                    </p>
                  </div>
                )}

                {/* Activity Logs Table */}
                {!isLoadingActivityLogs && activityLogs.length > 0 && (
                  <>
                    <div className="activity-logs-table-wrapper">
                      <table className="activity-logs-table">
                        <thead>
                          <tr>
                            <th>תאריך ושעה</th>
                            <th>מטפל</th>
                            <th>פעולה</th>
                            <th>פרטים</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activityLogs.map((log) => (
                            <tr key={log.id}>
                              <td className="datetime-cell">
                                {formatActivityDateTime(log.createdAt)}
                              </td>
                              <td className="therapist-cell">
                                <div className="therapist-info">
                                  <User size={16} weight="fill" />
                                  <span>{log.therapistName}</span>
                                </div>
                              </td>
                              <td className="action-cell">
                                <div className={`action-badge ${getActivityActionClass(log.action)}`}>
                                  {getActivityActionIcon(log.action)}
                                  <span>{log.actionLabel || ACTION_LABELS[log.action] || log.action}</span>
                                </div>
                              </td>
                              <td className="details-cell">
                                <div className="details-content">
                                  {log.entityName && (
                                    <span className="entity-name">{log.entityName}</span>
                                  )}
                                  {log.details && Object.keys(log.details).length > 0 && (
                                    <span className="details-extra">
                                      {log.details.startTime && (
                                        <span className="detail-item">
                                          {new Date(log.details.startTime).toLocaleString("he-IL", {
                                            day: "numeric",
                                            month: "short",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </span>
                                      )}
                                      {log.details.newStatus && (
                                        <span className="detail-item status">
                                          סטטוס: {log.details.newStatus}
                                        </span>
                                      )}
                                      {log.details.scope && (
                                        <span className="detail-item scope">
                                          {log.details.scope === 'personal' ? 'אישי' : 'קליניקה'}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={handleActivityPrevPage}
                        disabled={activityCurrentPage === 1 || isFetchingActivityLogs}
                      >
                        <CaretRight size={18} weight="bold" />
                        <span>הקודם</span>
                      </button>

                      <span className="pagination-info">
                        עמוד {activityCurrentPage}
                        {isFetchingActivityLogs && <LoaderLoop size="xs" />}
                      </span>

                      <button
                        className="pagination-btn"
                        onClick={handleActivityNextPage}
                        disabled={!activityHasMore || isFetchingActivityLogs}
                      >
                        <span>הבא</span>
                        <CaretLeft size={18} weight="bold" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Invoice Modal */}
      <InvoiceQuickModal
        isOpen={showCreateInvoiceModal}
        onClose={() => setShowCreateInvoiceModal(false)}
        onSuccess={handleInvoiceSuccess}
      />

      {/* Morning Setup Wizard */}
      <MorningSetupWizard
        isOpen={showMorningWizard}
        onClose={() => setShowMorningWizard(false)}
        onSuccess={() => {
        }}
      />
      {/* Morning settings editor (no credential re-entry) + disconnect */}
      <MorningSettingsModal
        isOpen={showMorningSettings}
        onClose={() => setShowMorningSettings(false)}
      />
      <MorningDisconnectModal
        isOpen={showMorningDisconnect}
        onClose={() => setShowMorningDisconnect(false)}
      />

      {/* Digital Invoice Setup Wizard */}
      <DigitalInvoiceSetupWizard
        isOpen={showDIWizard}
        onClose={() => setShowDIWizard(false)}
        onSuccess={() => {}}
      />

      {/* Digital Invoice Quick Modal */}
      <DigitalInvoiceQuickModal
        isOpen={showDICreateModal}
        onClose={() => setShowDICreateModal(false)}
        onSuccess={handleDIInvoiceSuccess}
      />

      {/* Support Modal */}
      {showSupportModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowSupportModal(false)}
        >
          <div className="support-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>צריכים עזרה?</h3>
              <ModalCloseButton onClick={() => setShowSupportModal(false)} />
            </div>

            {supportSent ? (
              <div className="support-success">
                <div className="success-icon">
                  <Heart size={48} weight="fill" />
                </div>
                <h4>מעולה! קיבלנו את הפנייה</h4>
                <p>ניצור איתך קשר בהקדם</p>
              </div>
            ) : (
              <>
                <div className="modal-body support-body">
                  <div className="support-illustration">
                    <Headset size={64} weight="duotone" />
                  </div>
                  <h4>אנחנו פה בשבילך!</h4>
                  <p className="support-text">
                    רוצים שצוות התמיכה שלנו יצור איתכם קשר?
                    <br />
                    את הפרטים שלכם כבר יש לנו - רק תאשרו ואנחנו על זה!
                  </p>
                  <div className="your-details">
                    <div className="detail-item">
                      <span className="detail-label">שם:</span>
                      <span className="detail-value">
                        {therapistProfile?.firstName}{" "}
                        {therapistProfile?.lastName}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">קליניקה:</span>
                      <span className="detail-value">
                        {clinicProfile?.name}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">טלפון:</span>
                      <span className="detail-value" dir="ltr">
                        {therapistProfile?.phone}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn-secondary"
                    onClick={() => setShowSupportModal(false)}
                    disabled={isSendingSupport}
                  >
                    עוד לא
                  </button>
                  <button
                    className="btn-primary support-confirm-btn"
                    onClick={handleSupportRequest}
                    disabled={isSendingSupport}
                  >
                    {isSendingSupport ? (
                      <>
                        <LoaderLoop size="xs" />
                        <span>שולח...</span>
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={18} weight="bold" />
                        <span>כן, צרו איתי קשר!</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={handleCloseCategoryModal}>
          <div className="category-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingCategory ? "עריכת קטגוריה" : "קטגוריה חדשה"}
                {!editingCategory && (
                  <span className="category-type-badge">
                    {categoryForm.type === "task" ? "משימות" : "תזכורות"}
                  </span>
                )}
              </h3>
              <ModalCloseButton onClick={handleCloseCategoryModal} />
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="required">שם הקטגוריה</label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, name: e.target.value })
                  }
                  placeholder="הכנס שם לקטגוריה"
                />
              </div>

              <div className="form-group">
                <label>צבע</label>
                <div className="color-picker">
                  {CATEGORY_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`color-option ${
                        categoryForm.color === color ? "selected" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        setCategoryForm({ ...categoryForm, color })
                      }
                    />
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="category-preview">
                <span className="preview-label">תצוגה מקדימה:</span>
                <span
                  className="preview-badge"
                  style={{
                    backgroundColor: categoryForm.color + "20",
                    color: categoryForm.color,
                  }}
                >
                  <Tag size={12} weight="bold" />
                  {categoryForm.name || "שם הקטגוריה"}
                </span>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleCloseCategoryModal}
                disabled={isCreatingCategory || isUpdatingCategory}
              >
                ביטול
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveCategory}
                disabled={
                  !categoryForm.name.trim() ||
                  isCreatingCategory ||
                  isUpdatingCategory
                }
              >
                {(isCreatingCategory || isUpdatingCategory) && <LoaderLoop size="xs" />}
                <span>{editingCategory ? "שמור שינויים" : "צור קטגוריה"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Treatment Type Modal (shared with Calendar Settings booking panel) */}
      {treatmentTypeModal && (
        <TreatmentTypeFormModal
          clinicId={clinicId}
          editingType={treatmentTypeModal.editing}
          onClose={() => setTreatmentTypeModal(null)}
        />
      )}

      {/* Treatment Tag Modal */}
      {showTreatmentTagModal && (
        <div className="modal-overlay" onClick={handleCloseTreatmentTagModal}>
          <div className="category-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTreatmentTag ? "עריכת תגית" : "תגית חדשה"}</h3>
              <ModalCloseButton onClick={handleCloseTreatmentTagModal} />
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>שם התגית</label>
                <input
                  type="text"
                  value={treatmentTagForm.name}
                  onChange={(e) =>
                    setTreatmentTagForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="לדוגמא: מעקב, דחוף, סיכום"
                />
              </div>

              <div className="form-group">
                <label>צבע</label>
                <div className="color-options">
                  {CATEGORY_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`color-option ${
                        treatmentTagForm.color === color ? "selected" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        setTreatmentTagForm((prev) => ({
                          ...prev,
                          color,
                        }))
                      }
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleCloseTreatmentTagModal}
              >
                ביטול
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveTreatmentTag}
                disabled={
                  !treatmentTagForm.name.trim() ||
                  isCreatingTreatmentTag ||
                  isUpdatingTreatmentTag
                }
              >
                {(isCreatingTreatmentTag || isUpdatingTreatmentTag) && <LoaderLoop size="xs" />}
                <span>{editingTreatmentTag ? "שמור שינויים" : "צור תגית"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game Modal - Speech Therapist Only */}
      {clinicProfile?.type === "speech_therapist" && showGameModal && (
        <div className="modal-overlay" onClick={handleCloseGameModal}>
          <div className="game-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingGame ? "עריכת משחק" : "הוספת משחק חדש"}</h3>
              <ModalCloseButton onClick={handleCloseGameModal} />
            </div>

            <div className="modal-body game-modal-body">
              <div className="form-group">
                <label className="required">שם המשחק</label>
                <input
                  type="text"
                  value={gameForm.name}
                  onChange={(e) =>
                    setGameForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="לדוגמה: קוביות, משחק זיכרון"
                />
              </div>

              <div className="form-row age-range-row">
                <div className="form-group">
                  <label>טווח גילאים</label>
                  <div className="age-inputs">
                    <div className="age-input-wrapper">
                      <span className="age-label">מגיל</span>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        value={gameForm.minAge}
                        onChange={(e) =>
                          setGameForm((prev) => ({
                            ...prev,
                            minAge: e.target.value,
                          }))
                        }
                        placeholder="0"
                      />
                    </div>
                    <span className="age-separator">עד</span>
                    <div className="age-input-wrapper">
                      <span className="age-label">גיל</span>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        value={gameForm.maxAge}
                        onChange={(e) =>
                          setGameForm((prev) => ({
                            ...prev,
                            maxAge: e.target.value,
                          }))
                        }
                        placeholder="99"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group goals-section">
                <label>מטרות המשחק</label>
                <p className="field-description">
                  בחר את המטרות הטיפוליות שהמשחק תורם להן
                </p>

                <div className="goals-categories">
                  {GAME_GOAL_CATEGORIES.map((category) => (
                    <div key={category.id} className="goal-category">
                      <button
                        type="button"
                        className={`category-header ${
                          expandedCategories.includes(category.id)
                            ? "expanded"
                            : ""
                        }`}
                        onClick={() => handleToggleCategory(category.id)}
                      >
                        <span
                          className="category-color-dot"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="category-name">{category.name}</span>
                        <span className="selected-count">
                          {
                            category.goals.filter((g) =>
                              gameForm.goalIds.includes(g.id)
                            ).length
                          }
                          /{category.goals.length}
                        </span>
                        <CaretRight
                          size={16}
                          weight="bold"
                          className="expand-icon"
                        />
                      </button>

                      {expandedCategories.includes(category.id) && (
                        <div className="category-goals">
                          {category.goals.map((goal) => (
                            <label
                              key={goal.id}
                              className={`goal-checkbox-item ${
                                gameForm.goalIds.includes(goal.id)
                                  ? "checked"
                                  : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={gameForm.goalIds.includes(goal.id)}
                                onChange={() => handleToggleGoal(goal.id)}
                              />
                              <span
                                className="checkbox-custom"
                                style={{
                                  borderColor: category.color,
                                  backgroundColor: gameForm.goalIds.includes(
                                    goal.id
                                  )
                                    ? category.color
                                    : "transparent",
                                }}
                              >
                                {gameForm.goalIds.includes(goal.id) && (
                                  <Check size={12} weight="bold" />
                                )}
                              </span>
                              <span className="goal-label">{goal.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {gameForm.goalIds.length > 0 && (
                  <div className="selected-goals-preview">
                    <span className="preview-label">
                      נבחרו {gameForm.goalIds.length} מטרות
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleCloseGameModal}
                disabled={isCreatingGame || isUpdatingGame}
              >
                ביטול
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveGame}
                disabled={
                  !gameForm.name.trim() || isCreatingGame || isUpdatingGame
                }
              >
                {(isCreatingGame || isUpdatingGame) && <LoaderLoop size="xs" />}
                <span>{editingGame ? "שמור שינויים" : "הוסף משחק"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Room Modal */}
      {showRoomModal && (
        <div className="modal-overlay" onClick={handleCloseRoomModal}>
          <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rm-modal-header">
              <div className="rm-modal-title">
                <span className="rm-modal-title-icon">
                  <Door size={20} weight="fill" />
                </span>
                <div className="rm-modal-title-text">
                  <span className="rm-modal-eyebrow">ניהול חדרים</span>
                  <h3>{editingRoom ? "עריכת חדר" : "הוספת חדר חדש"}</h3>
                </div>
              </div>
              <ModalCloseButton onClick={handleCloseRoomModal} />
            </div>

            <div className="rm-modal-body">
              <div className="rm-form-row">
                <div className="rm-form-group grow">
                  <label>
                    שם החדר <span className="rm-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={roomForm.name}
                    onChange={(e) =>
                      setRoomForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder='לדוגמה: "חדר טיפול 1", "חדר ירוק"'
                  />
                </div>

                <div className="rm-form-group compact">
                  <label>קיבולת</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={roomForm.capacity}
                    onChange={(e) =>
                      setRoomForm((prev) => ({
                        ...prev,
                        capacity: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="rm-form-group">
                <label>מטפל ברירת מחדל</label>
                <div className="rm-select-wrap">
                  <select
                    value={roomForm.defaultTherapistId}
                    onChange={(e) =>
                      setRoomForm((prev) => ({
                        ...prev,
                        defaultTherapistId: e.target.value,
                      }))
                    }
                  >
                    <option value="">ללא מטפל ברירת מחדל (חדר משותף)</option>
                    {therapists
                      .filter(isAssignableTherapist)
                      .map((therapist) => (
                        <option key={therapist.uid} value={therapist.uid}>
                          {therapist.firstName} {therapist.lastName}
                        </option>
                      ))}
                  </select>
                  <CaretDown size={14} className="rm-select-caret" />
                </div>
                <span className="rm-hint">
                  אם ישויך מטפל, החדר יוצע לו אוטומטית בעת קביעת פגישה
                </span>
              </div>

              <div className="rm-toggles">
                <label
                  className={`rm-toggle-card ${
                    roomForm.allowOthersToBook ? "on" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={roomForm.allowOthersToBook}
                    onChange={(e) =>
                      setRoomForm((prev) => ({
                        ...prev,
                        allowOthersToBook: e.target.checked,
                      }))
                    }
                  />
                  <span className="rm-toggle-check" />
                  <span className="rm-toggle-text">
                    <span className="rm-toggle-title">
                      <Users size={14} weight="fill" />
                      פתוח לכל המטפלים
                    </span>
                    <span className="rm-toggle-sub">
                      {roomForm.allowOthersToBook
                        ? "כל המטפלים יוכלו לקבוע פגישות בחדר זה כשהוא פנוי"
                        : "רק המטפל המשויך ומנהל הקליניקה יוכלו לקבוע פגישות בחדר זה"}
                    </span>
                  </span>
                </label>

                <label
                  className={`rm-toggle-card ${
                    roomForm.isAccessible ? "on" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={roomForm.isAccessible}
                    onChange={(e) =>
                      setRoomForm((prev) => ({
                        ...prev,
                        isAccessible: e.target.checked,
                      }))
                    }
                  />
                  <span className="rm-toggle-check" />
                  <span className="rm-toggle-text">
                    <span className="rm-toggle-title">
                      <WheelchairMotion size={14} weight="fill" />
                      חדר נגיש
                    </span>
                    <span className="rm-toggle-sub">
                      החדר יסומן כנגיש לבעלי מוגבלות
                    </span>
                  </span>
                </label>

                <label
                  className={`rm-toggle-card ${
                    roomForm.blockOnHolidays ? "on" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={roomForm.blockOnHolidays}
                    onChange={(e) =>
                      setRoomForm((prev) => ({
                        ...prev,
                        blockOnHolidays: e.target.checked,
                      }))
                    }
                  />
                  <span className="rm-toggle-check" />
                  <span className="rm-toggle-text">
                    <span className="rm-toggle-title">
                      <Prohibit size={14} weight="fill" />
                      חסימה אוטומטית בחגים
                    </span>
                    <span className="rm-toggle-sub">
                      {roomForm.blockOnHolidays
                        ? "החדר ייחסם אוטומטית בחגים ובימי שבתון"
                        : "החדר יישאר זמין גם בחגים"}
                    </span>
                  </span>
                </label>

                <label
                  className={`rm-toggle-card ${
                    roomForm.hasCustomAddress ? "on" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={roomForm.hasCustomAddress}
                    onChange={(e) =>
                      setRoomForm((prev) => ({
                        ...prev,
                        hasCustomAddress: e.target.checked,
                      }))
                    }
                  />
                  <span className="rm-toggle-check" />
                  <span className="rm-toggle-text">
                    <span className="rm-toggle-title">
                      <MapPin size={14} weight="fill" />
                      לחדר זה כתובת שונה מכתובת הקליניקה
                    </span>
                    <span className="rm-toggle-sub">
                      הכתובת תוצג למטופל בקישור אישור ההגעה במקום כתובת
                      הקליניקה
                    </span>
                  </span>
                </label>
              </div>

              {roomForm.hasCustomAddress && (
                <div className="rm-form-group">
                  <label>
                    כתובת החדר <span className="rm-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={roomForm.customAddress}
                    onChange={(e) =>
                      setRoomForm((prev) => ({
                        ...prev,
                        customAddress: e.target.value,
                      }))
                    }
                    placeholder='לדוגמה: "הרצל 12, תל אביב"'
                  />
                </div>
              )}

              <div className="rm-modal-info">
                <Info size={16} weight="fill" />
                <span>
                  את שעות הזמינות של החדר מגדירים דרך ״לוח זמינות״ בכרטיס החדר.
                </span>
              </div>
            </div>

            <div className="rm-modal-footer">
              <button
                type="button"
                className="rm-btn secondary"
                onClick={handleCloseRoomModal}
                disabled={isCreatingRoom || isUpdatingRoom}
              >
                ביטול
              </button>
              <button
                type="button"
                className="rm-btn primary"
                onClick={handleSaveRoom}
                disabled={
                  !roomForm.name.trim() || isCreatingRoom || isUpdatingRoom
                }
              >
                {(isCreatingRoom || isUpdatingRoom) && <LoaderLoop size="xs" />}
                <span>{editingRoom ? "שמירת שינויים" : "יצירת חדר"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Room Schedule Editor */}
      {scheduleRoom && (
        <RoomScheduleEditor
          clinicId={clinicId}
          room={scheduleRoom}
          onClose={() => setScheduleRoom(null)}
          therapistId={therapistProfile?.uid}
          therapistName={`${therapistProfile?.firstName} ${therapistProfile?.lastName}`}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="danger"
        icon="delete"
        confirmText="מחק"
      />

      {/* Patient sharing mode — confirm dialogs */}
      <ConfirmDialog
        isOpen={shareConfirm !== null}
        onClose={closeShareConfirm}
        onConfirm={confirmShareDialog}
        title={
          shareConfirm?.kind === "treatments"
            ? "להפסיק שיתוף דוחות טיפול?"
            : shareConfirm?.nextMode === "all"
              ? "לשתף את כל המטופלים עם כל הצוות?"
              : "לחזור למטופלים משויכים בלבד?"
        }
        message={
          shareConfirm?.kind === "treatments"
            ? "המטפלים יראו רק דוחות שכתבו בעצמם."
            : shareConfirm?.nextMode === "all"
              ? `כל המטפלים הפעילים (${activeTherapistCount}) יקבלו גישה ל-${sharePatientCount === null ? "…" : sharePatientCount} מטופלים. הפעולה נרשמת ביומן הפעילות.`
              : "המטפלים יחזרו לראות רק מטופלים משויכים. מטופלים ללא מטפל/ת פעיל/ה יופיעו רק למנהלים."
        }
        confirmText={shareConfirm?.kind === "treatments" ? "הפסקת שיתוף" : "אישור"}
        type="warning"
        icon="warning"
      />
    </div>
  );
}

export default ClinicSettingsPage;
